import { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Zap, Clock, Trash2, ChevronDown, ChevronUp,
  Edit3, Square, TrendingUp, TrendingDown, Minus,
  Activity, Send, FileText, AlertTriangle, CheckCircle,
  XCircle, Loader2, RefreshCw, LogOut, ArrowLeft, X, Info,
} from 'lucide-react';
import {
  useStrategies, useDeployStrategy, useDeleteStrategy,
  useStopDeployment, useExitAndStop, usePauseDeployment, useResumeDeployment,
  useExitAndStartDeployment,
  useDeploymentState, useDeploymentSymbolStatus, useRuntimeSnapshot, useSignals, useDispatchLogs, useRuntimeLogs, useUpdateStrategy,
  useBrokerCurrent,
  useMappedStrategies, useUpdateMappedStrategy,
  useMasterStrategyStatus, useActivateMasterStrategy, useDeactivateMasterStrategy,
  useMasterForceExitCandidates, useMasterForceExitUsers, useMasterForceExit,
} from '../../api/hooks';
import { SectionHeader, EmptyState, ErrorState, Spinner, ConfirmModal } from '../../components/ui';
import BottomSheet from '../../components/ui/BottomSheet';
import { fmtDateTime, fmtPrice, fmtRelative, symbolShort, cn } from '../../lib/utils';
import { mapStrategyToCardSummary, mapMappedStrategyToCardSummary } from '../../lib/strategyPreview';
import { mappingStatusBadgeClass, STRATEGY_MODES } from '../../lib/strategyModes';
import { strategiesApi } from '../../api/client';
import { toast } from '../../store';
import { ROUTES } from '../../routes/constants';
import { BrokerTokenGateModal } from '../broker/BrokerTokenGateModal';
import { fmtPnl } from '../broker/bookUtils';
import { useLtpStore } from '../../store/ltpStore';

// Below-lg viewport check, mirroring the app's established mobile/tablet cutoff
// (M1D onward). Used only to decide whether a Deploy tap should be gated behind
// the mobile LIVE-deploy confirmation sheet — desktop keeps firing immediately.
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia?.('(max-width: 1023.98px)').matches ?? false
  );
  useEffect(() => {
    const mql = window.matchMedia?.('(max-width: 1023.98px)');
    if (!mql) return;
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

// ── PnL calculator ────────────────────────────────────────────────────────────
function calcPnl(signals = []) {
  let total = 0;
  let trades = 0;
  let entry = null;
  const sorted = [...signals].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  for (const s of sorted) {
    if (!s.was_dispatched) continue;
    if (s.action === 'LE' || s.action === 'SE') {
      entry = { price: s.signal_price, action: s.action, qty: s.quantity || 1 };
    } else if ((s.action === 'LX' || s.action === 'SX') && entry) {
      const diff = s.action === 'LX'
        ? (s.signal_price - entry.price) * entry.qty
        : (entry.price - s.signal_price) * entry.qty;
      total += diff;
      trades++;
      entry = null;
    }
  }
  return { total, trades };
}

function ltpSymbolCandidates(symbol) {
  const raw = String(symbol || '').trim().toUpperCase();
  if (!raw) return [];
  const ticker = raw.includes(':') ? raw.split(':').pop() : raw;
  const candidates = [raw];
  if (/(\d+(CE|PE)|FUT)$/.test(ticker)) {
    candidates.push(`NSE:${ticker}`);
    candidates.push(`NFO:${ticker}`);
    candidates.push(ticker);
  }
  return Array.from(new Set(candidates));
}

function compactPendingStatus(pendingOrder) {
  const token = String(pendingOrder || '').trim().split(/\s+/)[0]?.toUpperCase();
  return token || null;
}

function isManualTradingStrategy(strategy) {
  const rawConfig = strategy?.config_json || strategy?.config || {};
  return (
    String(rawConfig?.strategy_source || '').trim().toUpperCase() === 'MANUAL'
    && String(rawConfig?.category || '').trim().toUpperCase() === 'MANUAL_TRADING'
  );
}

const FILTERS = ['All', 'Running', 'Not Running'];
const WEEKDAYS = [
  { label: 'M', bit: 1 << 0 },
  { label: 'T', bit: 1 << 1 },
  { label: 'W', bit: 1 << 2 },
  { label: 'T', bit: 1 << 3 },
  { label: 'F', bit: 1 << 4 },
];
const DEFAULT_DAYS_MASK = 0b11111;

function normalizeLifecycleStatus(value) {
  const token = String(value || '').trim().toUpperCase();
  if (!token) return 'IDLE';
  return token;
}

const DELETE_ELIGIBLE_LIFECYCLE_STATUSES = new Set(['IDLE', 'STOPPED', 'FAILED', 'DISABLED']);

function isStrategyDeleteAllowed({ activeDeploymentId, lifecycleStatus, schedulerEnabled }) {
  const lifecycle = normalizeLifecycleStatus(lifecycleStatus);
  return !activeDeploymentId && !schedulerEnabled && DELETE_ELIGIBLE_LIFECYCLE_STATUSES.has(lifecycle);
}

function isWithinScheduleWindow({ startTime, endTime, daysMask, timezone }) {
  const start = String(startTime || '').trim();
  const end = String(endTime || '').trim();
  if (!start || !end) return false;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  if (!Number.isInteger(sh) || !Number.isInteger(sm) || !Number.isInteger(eh) || !Number.isInteger(em)) return false;

  const tz = String(timezone || 'Asia/Kolkata').trim() || 'Asia/Kolkata';
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(now);
  const weekday = (parts.find((p) => p.type === 'weekday')?.value || '').toLowerCase();
  const hour = Number(parts.find((p) => p.type === 'hour')?.value || '0');
  const minute = Number(parts.find((p) => p.type === 'minute')?.value || '0');
  const weekdayIdx = { mon: 0, tue: 1, wed: 2, thu: 3, fri: 4 }[weekday];
  if (weekdayIdx == null) return false;

  const effectiveMask = Number.isInteger(daysMask) ? daysMask : DEFAULT_DAYS_MASK;
  if ((effectiveMask & (1 << weekdayIdx)) === 0) return false;

  const currentMins = (hour * 60) + minute;
  const startMins = (sh * 60) + sm;
  const endMins = (eh * 60) + em;
  return currentMins >= startMins && currentMins < endMins;
}

function StatusBadge({ lifecycleStatus, isLive, executionMode }) {
  const lifecycle = normalizeLifecycleStatus(lifecycleStatus);
  const token = String(executionMode || '').toUpperCase();
  if (lifecycle === 'SCHEDULED') {
    return (
      <span className="strat-chip flex items-center gap-1 text-2xs text-info bg-info/10 border border-info/25 rounded px-1.5 py-0.5">
        <span className="strat-dot w-1.5 h-1.5 rounded-full bg-info" />SCHEDULED
      </span>
    );
  }
  if (isLive && token === 'PAPER') {
    return (
      <span className="strat-chip flex items-center gap-1 text-2xs text-info bg-info/10 border border-info/25 rounded px-1.5 py-0.5">
        <span className="strat-dot w-1.5 h-1.5 rounded-full bg-info animate-pulse" />Paper Trade
      </span>
    );
  }
  return isLive ? (
    <span className="strat-chip flex items-center gap-1 text-2xs text-bull bg-bull/10 border border-bull/25 rounded px-1.5 py-0.5">
      <span className="strat-dot w-1.5 h-1.5 rounded-full bg-bull animate-pulse" />LIVE
    </span>
  ) : (
    <span className="strat-chip text-2xs text-txt-muted bg-bg-hover border border-bg-border rounded px-1.5 py-0.5">
      IDLE
    </span>
  );
}

const EXEC_MODE_LS_KEY = 'algosys:execMode';

function normalizeExecutionMode(value) {
  return String(value || 'PAPER').trim().toUpperCase() === 'LIVE' ? 'LIVE' : 'PAPER';
}

function derivePreferredExecutionMode(strategy) {
  return normalizeExecutionMode(
    strategy?.config?.ui_state?.preferred_execution_mode
    || strategy?.active_execution_mode
    || localStorage.getItem(EXEC_MODE_LS_KEY)
    || 'PAPER'
  );
}

function deriveSchedulerSettings(strategy) {
  const cfg = strategy?.config || {};
  const uiState = cfg.ui_state || {};
  const scheduler = uiState.scheduler || {};
  const globalRules = cfg.global_rules || {};
  return {
    enabled: !!scheduler.enabled,
    startTime: globalRules.start_time || '09:15',
    endTime: globalRules.end_time || '15:20',
    daysMask: Number.isInteger(scheduler.days_mask) ? scheduler.days_mask : DEFAULT_DAYS_MASK,
    timezone: scheduler.timezone || 'Asia/Kolkata',
  };
}

function getTodayIstDate() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

function PositionChip({ pos }) {
  const s = pos === 'LONG'
    ? { c: 'text-bull', bg: 'bg-bull/10 border-bull/25', I: TrendingUp }
    : pos === 'SHORT'
    ? { c: 'text-bear', bg: 'bg-bear/10 border-bear/25', I: TrendingDown }
    : { c: 'text-txt-muted', bg: 'bg-bg-hover border-bg-border', I: Minus };
  return (
    <span className={cn('strat-chip flex items-center gap-0.5 text-2xs font-mono rounded px-1.5 py-0.5 border', s.bg, s.c)}>
      <s.I size={9} />{pos || 'FLAT'}
    </span>
  );
}


function RuntimePulse({ snapshot }) {
  if (!snapshot) return null;
  const tone = snapshot.cooldown_active ? 'text-amber border-amber/25 bg-amber/10' : 'text-bull border-bull/25 bg-bull/10';
  return (
    <div className={cn('rounded border px-2 py-1.5 text-2xs font-mono', tone)}>
      <div className="flex items-center justify-between gap-2">
        <span>next: {snapshot.next_action}</span>
        <span>{snapshot.dedupe_status}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-2 text-[10px] text-txt-muted">
        <span>flat_only={String(snapshot.flat_only)}</span>
        {snapshot.stop_loss_percent != null && <span>SL={snapshot.stop_loss_percent}%</span>}
        {snapshot.target_percent != null && <span>TGT={snapshot.target_percent}%</span>}
        {snapshot.time_exit_time && <span>exit={snapshot.time_exit_time}</span>}
      </div>
      {snapshot.last_reason && (
        <div className="text-txt-muted truncate mt-1" title={snapshot.last_reason}>
          why: {snapshot.last_reason}
        </div>
      )}
    </div>
  );
}

// ── Sub-panels ─────────────────────────────────────────────────────────────────
function SignalsPanel({ depId, queryParams }) {
  const { data, isLoading } = useSignals(depId, queryParams);
  const signals = data?.signals || data || [];
  if (isLoading) return <div className="flex justify-center py-6"><Spinner size={16} /></div>;
  if (!signals.length) return <div className="text-center py-8 text-txt-muted text-xs font-mono">No signals yet — this strategy hasn't triggered any entries.</div>;
  return (
    <div className="space-y-1 max-h-52 overflow-y-auto">
      {signals.map((s) => (
        <div key={s.id} className="flex items-center gap-2 text-2xs font-mono bg-bg-base rounded px-2 py-1.5 border border-bg-border">
          <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', s.direction === 'BULLISH' ? 'bg-bull' : 'bg-bear')} />
          <span className={cn('font-semibold w-6', s.direction === 'BULLISH' ? 'text-bull' : 'text-bear')}>{s.action || '—'}</span>
          <span className="text-txt-secondary truncate flex-1 min-w-0">{s.option_symbol || '—'}</span>
          <span className="text-txt-muted shrink-0">₹{s.signal_price?.toFixed(1) ?? '—'}</span>
          {s.was_dispatched
            ? <CheckCircle size={11} className="text-bull shrink-0" />
            : <span className="text-bear/70 text-2xs shrink-0">{s.skip_reason?.slice(0, 12) || 'skip'}</span>}
          <span className="text-txt-muted/60 shrink-0">{fmtDateTime(s.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

function DispatchPanel({ depId }) {
  const { data, isLoading } = useDispatchLogs(depId);
  const logs = data?.logs || data || [];
  if (isLoading) return <div className="flex justify-center py-6"><Spinner size={16} /></div>;
  if (!logs.length) return <div className="text-center py-8 text-txt-muted text-xs font-mono">No dispatch logs yet — webhook alerts will appear here.</div>;
  return (
    <div className="space-y-1 max-h-52 overflow-y-auto">
      {logs.map((l) => (
        <div key={l.id} className="text-2xs font-mono bg-bg-base rounded px-2 py-1.5 border border-bg-border">
          <div className="flex items-center gap-2">
            {l.status === 'SUCCESS' ? <CheckCircle size={11} className="text-bull shrink-0" />
              : l.status === 'FAILED' ? <XCircle size={11} className="text-bear shrink-0" />
              : <Loader2 size={11} className="text-amber shrink-0 animate-spin" />}
            <span className={cn('font-semibold', l.status === 'SUCCESS' ? 'text-bull' : l.status === 'FAILED' ? 'text-bear' : 'text-amber')}>
              {l.status}
            </span>
            <span className="text-txt-muted">HTTP {l.http_status ?? '—'}</span>
            <span className="text-txt-muted/60 ml-auto">{fmtRelative(l.dispatched_at)}</span>
          </div>
          {(l.error_msg || l.response_body) && (
            <div className="text-txt-muted/70 truncate mt-0.5">{l.error_msg || l.response_body}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function StatusPanel({ depId, strategyId, executionMode, qtyMode }) {
  const { data, isLoading, refetch } = useDeploymentSymbolStatus(depId);
  const ltpMap = useLtpStore((s) => s.ltp);
  const subscribe = useLtpStore((s) => s.subscribe);
  const unsubscribe = useLtpStore((s) => s.unsubscribe);
  const [exitDraft, setExitDraft] = useState(null);
  const [exitPrice, setExitPrice] = useState('');
  const [exitQty, setExitQty] = useState('');
  const [exitBusy, setExitBusy] = useState(false);
  const partialAllowed = String(qtyMode || '').toUpperCase() === 'BY_ALERT';
  const rows = data?.rows || [];
  const pnlSymbols = useMemo(() => rows
    .filter((row) => ['LONG', 'SHORT'].includes(String(row.position_status || '').toUpperCase()))
    .flatMap((row) => (Array.isArray(row.ltp_symbols) && row.ltp_symbols.length ? row.ltp_symbols : ltpSymbolCandidates(row.symbol)))
    .filter(Boolean), [rows]);
  const pnlSymbolsKey = JSON.stringify(pnlSymbols);

  useEffect(() => {
    if (executionMode !== 'LIVE' || pnlSymbols.length === 0) return undefined;
    subscribe(pnlSymbols);
    return () => unsubscribe(pnlSymbols);
  }, [executionMode, pnlSymbolsKey]);

  const openExitModal = async (row) => {
    setExitBusy(true);
    try {
      const quote = await strategiesApi.manualExitQuote({
        strategy_id: strategyId,
        deployment_id: depId,
        symbol_scope: row.symbol,
      });
      setExitDraft({ row, quote });
      setExitPrice('');
      // Pre-fill qty input with the row's open qty (default = full exit).
      // Editable only for BY_ALERT strategies; the input is hidden otherwise.
      const openQty = Number(row.open_qty);
      setExitQty(Number.isFinite(openQty) && openQty > 0 ? String(openQty) : '');
    } catch (err) {
      toast.error(err.message || 'Could not resolve exit price');
    } finally {
      setExitBusy(false);
    }
  };

  const openReconcileModal = (row) => {
    setExitDraft({ row, quote: null, reconcileOnly: true });
    setExitPrice('');
    setExitQty('');
  };

  const submitExit = async () => {
    if (!exitDraft) return;
    setExitBusy(true);
    try {
      const openQty = Number(exitDraft.row.open_qty);
      const userQty = Number(exitQty);
      // Send qty only when partial is allowed AND the user reduced it below
      // the open qty. Backend will ignore qty for non-BY_ALERT modes anyway.
      const sendPartialQty =
        partialAllowed
        && Number.isFinite(userQty) && userQty > 0
        && Number.isFinite(openQty) && openQty > 0
        && userQty < openQty;
      const res = await strategiesApi.manualExit({
        strategy_id: strategyId,
        deployment_id: depId,
        symbol_scope: exitDraft.row.symbol,
        ...(!exitDraft.reconcileOnly && sendPartialQty ? { qty: Math.floor(userQty) } : {}),
        ...(!exitDraft.reconcileOnly && exitPrice ? { price: Number(exitPrice) } : {}),
      });
      if (res.outcome === 'exit_order_placed') toast.success(res.message);
      else toast.warn(res.message);
      setExitDraft(null);
      setExitPrice('');
      setExitQty('');
      refetch();
    } catch (err) {
      toast.error(err.message || 'Exit request failed');
    } finally {
      setExitBusy(false);
    }
  };

  if (isLoading) return <div className="flex justify-center py-6"><Spinner size={16} /></div>;
  if (!rows.length) return <div className="text-center py-8 text-txt-muted text-xs font-mono">No open positions for this strategy yet.</div>;
  return (
    <>
      <div className="max-h-52 overflow-auto">
        <table className="w-full text-2xs font-mono">
          <thead className="sticky top-0 bg-bg-surface text-txt-label uppercase tracking-widest">
            <tr>
              {['Symbol', 'Entry', 'Time', 'Status', 'P/L', 'Exit'].map((label) => (
                <th key={label} className="text-left font-semibold px-2 py-1 border-b border-bg-border whitespace-nowrap">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const state = String(row.position_status || 'FLAT').toUpperCase();
              const stateColor = state === 'LONG' ? 'text-bull' : state === 'SHORT' ? 'text-bear' : 'text-txt-muted';
              const isPendingOnly = !!row.pending_order && !['LONG', 'SHORT'].includes(state);
              const canExit = ['LONG', 'SHORT'].includes(state) || isPendingOnly;
              const entryRef = row.entry_price_reference == null ? '—' : fmtPrice(row.entry_price_reference);
              const rowStatus = compactPendingStatus(row.pending_order) || state;
              const entryPrice = Number(row.entry_price_reference);
              const openQty = Number(row.open_qty);
              const candidates = Array.isArray(row.ltp_symbols) && row.ltp_symbols.length ? row.ltp_symbols : ltpSymbolCandidates(row.symbol);
              const ltpRaw = candidates.map((symbol) => ltpMap[symbol]).find((value) => value != null);
              const ltp = Number(ltpRaw);
              const side = String(row.side || '').toUpperCase();
              const isLongPrice = side === 'BUY' || (!side && state === 'LONG');
              const pnlValue = (executionMode === 'LIVE' && !row.pending_order && (state === 'LONG' || state === 'SHORT')
                && Number.isFinite(entryPrice) && entryPrice > 0
                && Number.isFinite(openQty) && openQty > 0
                && Number.isFinite(ltp) && ltp > 0)
                ? (isLongPrice ? (ltp - entryPrice) * openQty : (entryPrice - ltp) * openQty)
                : null;
              const pnlNumber = Number(pnlValue);
              const pnlColor = Number.isFinite(pnlNumber) && pnlNumber > 0
                ? 'text-bull'
                : Number.isFinite(pnlNumber) && pnlNumber < 0
                  ? 'text-bear'
                  : 'text-txt-muted';
              return (
                <tr key={row.symbol} className="border-b border-bg-border/60 last:border-0">
                  <td className="px-2 py-1.5 text-txt-primary whitespace-nowrap">{symbolShort(row.symbol) || row.symbol}</td>
                  <td className="px-2 py-1.5 text-txt-muted whitespace-nowrap">{entryRef}</td>
                  <td className="px-2 py-1.5 text-txt-muted whitespace-nowrap">{row.time ? fmtDateTime(row.time) : '—'}</td>
                  <td className={cn('px-2 py-1.5 whitespace-nowrap', row.pending_order ? 'text-amber' : stateColor)}>{rowStatus}</td>
                  <td className={cn('px-2 py-1.5 whitespace-nowrap', pnlColor)}>{pnlValue == null ? '—' : fmtPnl(pnlValue)}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <button
                      type="button"
                      disabled={!canExit || exitBusy}
                      onClick={() => (isPendingOnly ? openReconcileModal(row) : openExitModal(row))}
                      className={cn(
                        'rounded px-2 py-0.5 text-2xs font-mono border transition-colors',
                        canExit
                          ? 'bg-bear/10 border-bear/30 text-bear hover:bg-bear/20'
                          : 'bg-bg-hover border-bg-border text-txt-muted/50 cursor-not-allowed'
                      )}
                      title={isPendingOnly ? 'Cancel pending live order for this symbol' : 'Exit this symbol'}
                    >
                      {isPendingOnly ? 'CANCEL' : 'EXIT'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {exitDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="card p-4 w-full max-w-xs animate-fade-in shadow-panel">
            <h3 className="font-semibold text-txt-primary text-sm mb-1">{exitDraft.reconcileOnly ? 'Confirm Cancel' : 'Confirm Exit'}</h3>
            <p className="text-2xs font-mono text-txt-secondary mb-3">
              {exitDraft.reconcileOnly ? exitDraft.row.symbol : `${exitDraft.quote.action} ${exitDraft.row.symbol}`}
            </p>
            {exitDraft.reconcileOnly ? (
              <div className="text-2xs font-mono text-txt-muted space-y-1 mb-3">
                <div>Pending order: <span className="text-amber">{compactPendingStatus(exitDraft.row.pending_order) || 'AWAITING'}</span></div>
                <div>Are you sure to cancel the pending order? Any filled position will also be exited if found.</div>
              </div>
            ) : (
              <>
                <div className="text-2xs font-mono text-txt-muted space-y-1 mb-3">
                  <div>LTP: <span className="text-txt-primary">{fmtPrice(exitDraft.quote.ltp)}</span> via {exitDraft.quote.ltp_source}</div>
                  <div>MPP: <span className="text-txt-primary">{exitDraft.quote.mpp}%</span> · limit approx <span className="text-amber">{fmtPrice(exitDraft.quote.limit_price)}</span></div>
                  <div>
                    Open qty: <span className="text-txt-primary">{exitDraft.row.open_qty ?? '—'}</span>
                    {!partialAllowed && <span className="ml-1 text-txt-muted/70">(full exit; partial allowed only for BY_ALERT)</span>}
                  </div>
                </div>
                {partialAllowed && (
                  <label className="block text-2xs font-mono text-txt-muted mb-1">Exit qty (≤ open qty)</label>
                )}
                <input
                  type="number"
                  min="1"
                  step="1"
                  max={exitDraft.row.open_qty ?? undefined}
                  className={cn(
                    "input-base w-full text-xs font-mono mb-3",
                    !partialAllowed && "opacity-60 cursor-not-allowed"
                  )}
                  placeholder={partialAllowed ? "Lower this to partial-exit; leave for full" : "Read-only — full exit"}
                  value={exitQty}
                  onChange={(e) => setExitQty(e.target.value)}
                  disabled={exitBusy || !partialAllowed}
                  readOnly={!partialAllowed}
                />
                <input
                  type="number"
                  min="0"
                  step="0.05"
                  className="input-base w-full text-xs font-mono mb-3"
                  placeholder="Optional base price; blank uses fresh LTP"
                  value={exitPrice}
                  onChange={(e) => setExitPrice(e.target.value)}
                  disabled={exitBusy}
                />
              </>
            )}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setExitDraft(null)} disabled={exitBusy} className="btn-ghost text-xs">Cancel</button>
              <button type="button" onClick={submitExit} disabled={exitBusy} className="btn-danger text-xs flex items-center gap-1.5">
                {exitBusy && <Spinner size={12} />} {exitDraft.reconcileOnly ? 'Confirm Cancel' : 'Confirm Exit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function RuntimePanel({ depId, queryParams }) {
  const { data, isLoading } = useRuntimeLogs(depId, queryParams);
  const logs = data?.logs || data || [];
  if (isLoading) return <div className="flex justify-center py-6"><Spinner size={16} /></div>;
  if (!logs.length) return <div className="text-center py-8 text-txt-muted text-xs font-mono">No runtime logs yet — execution details appear here once the strategy runs.</div>;
  return (
    <div className="space-y-1 max-h-52 overflow-y-auto">
      {logs.map((l) => (
        <div key={l.id} className="flex items-start gap-2 text-2xs font-mono bg-bg-base rounded px-2 py-1.5 border border-bg-border">
          <span className={cn('shrink-0 font-semibold',
            l.level === 'ERROR' ? 'text-bear' : l.level === 'WARNING' ? 'text-amber' : 'text-txt-muted/60')}>
            {l.level?.slice(0, 4)}
          </span>
          <span className="text-txt-secondary flex-1 break-all">{l.message}</span>
          <span className="text-txt-muted/60 shrink-0">{fmtRelative(l.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

// ── Stop modal ─────────────────────────────────────────────────────────────────
function StopModal({ name, depState, onClose, onStop, onExitStop, stopping, allowExitStop = true, description = null }) {
  const hasPos = allowExitStop && depState?.position_status && depState.position_status !== 'FLAT';
  const modalNode = (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-bg-card border border-bg-border rounded-xl w-full max-w-[260px] shadow-2xl animate-fade-in">
        <div className="p-3.5 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className={hasPos ? "text-amber" : "text-bear"} />
            <h3 className="font-semibold text-txt-primary text-sm">Stop Strategy</h3>
          </div>
          <p className="text-2xs text-txt-secondary">
            {description || <>Stop <strong className="text-txt-primary">"{name}"</strong>?</>}
          </p>
          {hasPos && (
            <div className="text-2xs font-mono text-amber bg-amber/5 border border-amber/20 rounded px-2 py-1.5">
              ⚠ Open {depState.position_status} position detected
            </div>
          )}
          <div className="space-y-2 pt-1">
            {hasPos ? (
              <>
                <button onClick={onExitStop} disabled={stopping}
                  className="w-full btn-primary text-xs py-2.5 flex items-center justify-center gap-2">
                  {stopping ? <Spinner size={12} /> : <Send size={12} />}
                  Send Exit Alert + Stop
                </button>
                <button onClick={onStop} disabled={stopping}
                  className="w-full bg-bear/10 border border-bear/30 text-bear hover:bg-bear/20 rounded-md text-xs py-2.5 font-medium transition-colors flex items-center justify-center gap-2">
                  <Square size={12} /> Stop Without Closing
                </button>
              </>
            ) : (
              <button onClick={onStop} disabled={stopping}
                className="w-full bg-bear/10 border border-bear/30 text-bear hover:bg-bear/20 rounded-md text-xs py-2.5 font-medium transition-colors flex items-center justify-center gap-2">
                {stopping ? <Spinner size={12} /> : <Square size={12} />}
                Stop Strategy
              </button>
            )}
            <button onClick={onClose} className="w-full btn-ghost text-xs py-2">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
  if (typeof document === 'undefined') return modalNode;
  return createPortal(modalNode, document.body);
}

// ── Strategy Card ──────────────────────────────────────────────────────────────
function StrategyCard({ strategy, onDelete, onDeploy, deploying }) {
  const navigate  = useNavigate();
  const [accTab,    setAccTab]    = useState(null);
  const [showStop,  setShowStop]  = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeployConfirm, setShowDeployConfirm] = useState(false);
  const isMobile = useIsMobile();
  const [stopping,  setStopping]  = useState(false);
  const [notice, setNotice] = useState(null);
  const [selectedExecutionMode, setSelectedExecutionMode] = useState(() => derivePreferredExecutionMode(strategy));
  const [schedulerEnabled, setSchedulerEnabled] = useState(() => deriveSchedulerSettings(strategy).enabled);
  const [scheduleStartTime, setScheduleStartTime] = useState(() => deriveSchedulerSettings(strategy).startTime);
  const [scheduleEndTime, setScheduleEndTime] = useState(() => deriveSchedulerSettings(strategy).endTime);
  const [scheduleDaysMask, setScheduleDaysMask] = useState(() => deriveSchedulerSettings(strategy).daysMask);
  const [cancelPendingAtEnd, setCancelPendingAtEnd] = useState(
    () => strategy.latest_cancel_pending_at_end ?? strategy.config?.ui_state?.scheduler?.cancel_pending_at_end ?? false
  );

  const depId  = strategy.active_deployment_id;
  const latestDeploymentId = strategy.latest_deployment_id || depId;
  const lifecycleStatus = normalizeLifecycleStatus(strategy.latest_deployment_status || (depId ? 'ACTIVE' : 'IDLE'));
  const isScheduledLifecycle = lifecycleStatus === 'SCHEDULED';
  const isLive = lifecycleStatus === 'ACTIVE' && !!depId;
  const strategyMode = String(strategy.strategy_mode || '').toUpperCase();
  const isMasterExternal = strategyMode === 'MASTER_EXTERNAL';
  const isMasterManual = strategyMode === 'MASTER_MANUAL';
  const isMasterStrategy = isMasterExternal || isMasterManual;
  const canDeleteStrategy = isStrategyDeleteAllowed({ activeDeploymentId: depId, lifecycleStatus, schedulerEnabled });
  const showSchedulerControls = !isLive && !isMasterStrategy;
  const savedExecutionMode = derivePreferredExecutionMode(strategy);
  const savedScheduler = useMemo(() => deriveSchedulerSettings(strategy), [strategy]);
  const activeExecutionMode = normalizeExecutionMode(strategy.active_execution_mode);
  const isAutoMode = !!schedulerEnabled;
  const lockScheduleInputs = isScheduledLifecycle || isLive;
  const scheduleWindowOpen = isWithinScheduleWindow({
    startTime: strategy.latest_schedule_start_time || scheduleStartTime,
    endTime: strategy.latest_schedule_end_time || scheduleEndTime,
    daysMask: strategy.latest_schedule_days_mask ?? scheduleDaysMask,
    timezone: strategy.latest_schedule_timezone || savedScheduler.timezone || 'Asia/Kolkata',
  });
  const canStartInWindow = isAutoMode && isScheduledLifecycle && scheduleWindowOpen && !!latestDeploymentId;
  const scheduleActionLabel = isAutoMode ? (isScheduledLifecycle ? 'Cancel Schedule' : 'Schedule') : 'Deploy';
  const todayIst = getTodayIstDate();
  const todayQueryParams = useMemo(() => ({
    from_date: todayIst,
    to_date: todayIst,
  }), [todayIst]);

  const runtimeSnapshotEnabled = !!depId && !isManualTradingStrategy(strategy);
  const { data: depState }   = useDeploymentState(latestDeploymentId);
  const { data: symbolStatus } = useDeploymentSymbolStatus(latestDeploymentId);
  const { data: runtimeSnapshot } = useRuntimeSnapshot(depId, { enabled: runtimeSnapshotEnabled });
  const { data: signalsData } = useSignals(depId, todayQueryParams);

  const signals   = signalsData?.signals || signalsData || [];
  const symbolRows = symbolStatus?.rows || [];
  const openOrdersCount = symbolStatus?.open_orders_count ?? 0;
  const positionsCount = symbolStatus?.positions_count ?? symbolRows.filter((row) => (
    ['LONG', 'SHORT'].includes(String(row.position_status || '').toUpperCase())
  )).length;
  const startedAt = strategy.latest_started_at || strategy.latest_last_started_at || strategy.created_at;
  const lastSignalAt = signals.reduce((latest, signal) => {
    if (!signal?.created_at) return latest;
    if (!latest) return signal.created_at;
    return new Date(signal.created_at) > new Date(latest) ? signal.created_at : latest;
  }, null);
  const { total: pnl } = useMemo(() => calcPnl(signals), [signals]);

  const stopMut  = useStopDeployment();
  const exitStop = useExitAndStop();
  const pauseMut = usePauseDeployment();
  const resumeMut = useResumeDeployment();
  const updateMut = useUpdateStrategy();

  const [showForceExit, setShowForceExit] = useState(false);
  const [activating, setActivating]       = useState(false);
  const [deactivating, setDeactivating]   = useState(false);
  const [deactivateWarning, setDeactivateWarning] = useState(null);  // { blocking_children: [...] } when 409
  const activateMut   = useActivateMasterStrategy();
  const deactivateMut = useDeactivateMasterStrategy();
  const { data: masterStatus } = useMasterStrategyStatus(strategy.id, isMasterStrategy);

  const handleMasterActivate = async () => {
    setActivating(true);
    try {
      await activateMut.mutateAsync(strategy.id);
      showInlineNotice('Master strategy activated', 'success');
    } catch (e) { toast.error(e.message); }
    setActivating(false);
  };

  const _runDeactivate = async ({ confirmOpen }) => {
    setDeactivating(true);
    try {
      await deactivateMut.mutateAsync({ id: strategy.id, confirm_open_positions: !!confirmOpen });
      setDeactivateWarning(null);
      showInlineNotice(
        isMasterManual
          ? 'Manual Master parent deactivated. Child deployments and positions remain untouched.'
          : confirmOpen
            ? 'Master deactivated. Mapped users still hold open positions.'
            : 'Master deactivated. Mapped user deployments remain running.',
        'success',
      );
    } catch (e) {
      // Check for 409 with blocking-children detail.
      const detail = e?.response?.data?.detail || e?.detail || e?.data?.detail;
      const reason = (typeof detail === 'object') ? detail?.reason : null;
      const blocking = (typeof detail === 'object') ? detail?.blocking_children : null;
      if (e?.status === 409 && reason === 'open_positions' && Array.isArray(blocking)) {
        setDeactivateWarning({ blocking_children: blocking });
      } else {
        toast.error(e?.message || 'Deactivate failed');
      }
    }
    setDeactivating(false);
  };

  const handleMasterDeactivate = () => _runDeactivate({ confirmOpen: false });
  const handleConfirmDeactivateAnyway = () => _runDeactivate({ confirmOpen: true });

  const sm = mapStrategyToCardSummary(strategy);

  // Config-tile fields (Row 2) — extracted so the same fields can be rendered
  // both in the desktop grid and inside the mobile Details sheet.
  const configFields = useMemo(() => {
    const baseFields = [
      sm.instrument === 'EXTERNAL'
        ? { l: 'Segment', v: sm.segment || 'External', c: 'text-txt-primary' }
        : { l: 'Symbol', v: symbolShort(sm.symbol) || '—', c: 'text-txt-primary' },
      { l: 'Rule',   v: sm.ruleLabel,                  c: 'text-txt-primary' },
    ];
    let extraFields;
    if (isMasterManual) {
      const totalMapped  = masterStatus?.total_mapped_users  ?? '—';
      const activeMapped = masterStatus?.active_mapped_users ?? '—';
      extraFields = [
        { l: 'Mapped', v: totalMapped,  c: 'text-txt-primary' },
        { l: 'Active', v: activeMapped, c: activeMapped > 0 ? 'text-info' : 'text-txt-muted' },
      ];
    } else if (sm.isOptions) {
      extraFields = [
        { l: sm.totalLegs != null ? 'Legs' : 'Strike',
          v: sm.totalLegs != null ? sm.totalLegs : (sm.strike || '—'),
          c: 'text-txt-primary' },
        { l: 'Type', v: 'OPT', c: 'text-amber/70' },
      ];
    } else if (sm.hasFutures || sm.instrument === 'FUT') {
      extraFields = [
        { l: 'Qty',  v: sm.quantity ?? '—', c: 'text-txt-primary' },
        { l: 'Type', v: 'FUT',              c: 'text-amber/70'    },
      ];
    } else if (sm.instrument === 'EXTERNAL') {
      if (isMasterExternal) {
        const totalMapped  = masterStatus?.total_mapped_users  ?? '—';
        const activeMapped = masterStatus?.active_mapped_users ?? '—';
        extraFields = [
          { l: 'Mapped', v: totalMapped,  c: 'text-txt-primary' },
          { l: 'Active', v: activeMapped, c: activeMapped > 0 ? 'text-info' : 'text-txt-muted' },
        ];
      } else {
        const hasLong = symbolRows.some((row) => String(row.position_status || '').toUpperCase() === 'LONG');
        const hasShort = symbolRows.some((row) => String(row.position_status || '').toUpperCase() === 'SHORT');
        const positionsColor = hasLong && hasShort ? 'text-amber' : hasLong ? 'text-bull' : hasShort ? 'text-bear' : 'text-txt-muted';
        extraFields = [
          { l: 'Orders',    v: openOrdersCount, c: openOrdersCount > 0 ? 'text-amber' : 'text-txt-primary' },
          { l: 'Positions', v: positionsCount,  c: positionsColor },
        ];
      }
    } else {
      const typeLabel = sm.instrument === 'UNDERLYING' ? 'UND' : (sm.instrument || 'EQ');
      extraFields = [
        { l: 'Qty',  v: sm.quantity ?? '—', c: 'text-txt-primary' },
        { l: 'Type', v: typeLabel,           c: 'text-txt-muted'   },
      ];
    }
    return [...baseFields, ...extraFields];
  }, [sm, isMasterExternal, isMasterManual, masterStatus, symbolRows, openOrdersCount, positionsCount]);

  useEffect(() => {
    setSelectedExecutionMode(derivePreferredExecutionMode(strategy));
  }, [strategy.id, strategy.config?.ui_state?.preferred_execution_mode, strategy.active_execution_mode]);
  useEffect(() => {
    const next = deriveSchedulerSettings(strategy);
    setSchedulerEnabled(next.enabled);
    setScheduleStartTime(next.startTime);
    setScheduleEndTime(next.endTime);
    setScheduleDaysMask(next.daysMask);
  }, [strategy.id, savedScheduler.enabled, savedScheduler.startTime, savedScheduler.endTime, savedScheduler.daysMask]);

  const handleStop = async (withExit) => {
    setStopping(true);
    try {
      if (withExit) await exitStop.mutateAsync(depId);
      else          await stopMut.mutateAsync(depId);
      showInlineNotice(`"${strategy.name}" stopped`, 'success');
      setShowStop(false);
    } catch (e) { toast.error(e.message); }
    setStopping(false);
  };

  const toggleTab = (tab) => setAccTab(p => p === tab ? null : tab);
  const showInlineNotice = (message, type = 'info') => {
    setNotice({ message, type, ts: Date.now() });
  };
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const persistPreferredExecutionMode = async (mode) => {
    const normalizedMode = normalizeExecutionMode(mode);
    const nextConfig = {
      ...(strategy.config || {}),
      ui_state: {
        ...((strategy.config && strategy.config.ui_state) || {}),
        preferred_execution_mode: normalizedMode,
      },
    };
    await updateMut.mutateAsync({
      id: strategy.id,
      payload: { config: nextConfig },
    });
  };

  const buildSchedulerPatchedConfig = (nextScheduler) => {
    const currentConfig = strategy.config || {};
    const currentUiState = currentConfig.ui_state || {};
    const currentScheduler = currentUiState.scheduler || {};
    const currentGlobalRules = currentConfig.global_rules || {};
    return {
      ...currentConfig,
      global_rules: {
        ...currentGlobalRules,
        start_time: nextScheduler.startTime,
        end_time: nextScheduler.endTime,
      },
      ui_state: {
        ...currentUiState,
        scheduler: {
          ...currentScheduler,
          enabled: nextScheduler.enabled,
          days_mask: nextScheduler.daysMask,
          timezone: nextScheduler.timezone || 'Asia/Kolkata',
          cancel_pending_at_end: nextScheduler.cancel_pending_at_end ?? false,
        },
      },
    };
  };

  const persistSchedulerSettings = async (nextScheduler) => {
    const nextConfig = buildSchedulerPatchedConfig(nextScheduler);
    await updateMut.mutateAsync({
      id: strategy.id,
      payload: { config: nextConfig },
    });
  };

  const handleExecutionModeChange = async (nextMode) => {
    const normalizedMode = normalizeExecutionMode(nextMode);
    const previousMode = selectedExecutionMode;
    setSelectedExecutionMode(normalizedMode);
    localStorage.setItem(EXEC_MODE_LS_KEY, normalizedMode);
    try {
      await persistPreferredExecutionMode(normalizedMode);
    } catch (e) {
      setSelectedExecutionMode(previousMode);
      toast.error(e.message);
    }
  };

  const handleSchedulerToggle = async () => {
    if (lockScheduleInputs) return;
    const previous = schedulerEnabled;
    const nextEnabled = !schedulerEnabled;
    const nextScheduler = {
      enabled: nextEnabled,
      startTime: scheduleStartTime,
      endTime: scheduleEndTime,
      daysMask: scheduleDaysMask || DEFAULT_DAYS_MASK,
      timezone: savedScheduler.timezone || 'Asia/Kolkata',
      cancel_pending_at_end: cancelPendingAtEnd,
    };
    setSchedulerEnabled(nextEnabled);
    try {
      await persistSchedulerSettings(nextScheduler);
    } catch (e) {
      setSchedulerEnabled(previous);
      toast.error(e.message);
    }
  };

  const handleScheduleTimeBlur = async () => {
    if (lockScheduleInputs) return;
    const nextScheduler = {
      enabled: schedulerEnabled,
      startTime: scheduleStartTime,
      endTime: scheduleEndTime,
      daysMask: scheduleDaysMask || DEFAULT_DAYS_MASK,
      timezone: savedScheduler.timezone || 'Asia/Kolkata',
      cancel_pending_at_end: cancelPendingAtEnd,
    };
    try {
      await persistSchedulerSettings(nextScheduler);
    } catch (e) {
      setScheduleStartTime(savedScheduler.startTime);
      setScheduleEndTime(savedScheduler.endTime);
      toast.error(e.message);
    }
  };

  const toggleScheduleDay = async (bit) => {
    if (lockScheduleInputs) return;
    const nextMask = (scheduleDaysMask || DEFAULT_DAYS_MASK) ^ bit;
    if (nextMask === 0) return;
    const previousMask = scheduleDaysMask;
    setScheduleDaysMask(nextMask);
    const nextScheduler = {
      enabled: schedulerEnabled,
      startTime: scheduleStartTime,
      endTime: scheduleEndTime,
      daysMask: nextMask,
      timezone: savedScheduler.timezone || 'Asia/Kolkata',
      cancel_pending_at_end: cancelPendingAtEnd,
    };
    try {
      await persistSchedulerSettings(nextScheduler);
    } catch (e) {
      setScheduleDaysMask(previousMask);
      toast.error(e.message);
    }
  };

  const handleScheduleCancel = async () => {
    if (!latestDeploymentId) return;
    try {
      await pauseMut.mutateAsync(latestDeploymentId);
      showInlineNotice('Schedule cancelled', 'warn');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleScheduleStart = async () => {
    if (!latestDeploymentId) return;
    try {
      await resumeMut.mutateAsync(latestDeploymentId);
      showInlineNotice('Deployment started', 'success');
    } catch (e) {
      toast.error(e.message);
    }
  };

  // The actual deploy call — unchanged signature/behavior, just extracted so it
  // can be invoked either immediately (Paper) or after the LIVE-deploy
  // confirmation UI's "Deploy Live" button (see showDeployConfirm).
  const runDeploy = async () => {
    try {
      const result = await onDeploy(strategy, selectedExecutionMode, {
        enabled: schedulerEnabled,
        startTime: scheduleStartTime,
        endTime: scheduleEndTime,
        daysMask: scheduleDaysMask || DEFAULT_DAYS_MASK,
        timezone: savedScheduler.timezone || 'Asia/Kolkata',
        cancel_pending_at_end: cancelPendingAtEnd,
      });
      // handleDeploy returns undefined when gated (broker token missing/expired,
      // no active broker). Only confirm success when an actual deployment ran.
      if (result) {
        showInlineNotice(isAutoMode ? 'Schedule confirmed' : 'Deployment started', 'success');
      }
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleConfirmLiveDeploy = async () => {
    setShowDeployConfirm(false);
    await runDeploy();
  };

  const handleDeployClick = async () => {
    try {
      if (isAutoMode && isScheduledLifecycle) {
        await handleScheduleCancel();
        return;
      }
      if (selectedExecutionMode !== savedExecutionMode) {
        await persistPreferredExecutionMode(selectedExecutionMode);
      }
      const schedulerDirty =
        schedulerEnabled !== savedScheduler.enabled ||
        scheduleStartTime !== savedScheduler.startTime ||
        scheduleEndTime !== savedScheduler.endTime ||
        (scheduleDaysMask || DEFAULT_DAYS_MASK) !== (savedScheduler.daysMask || DEFAULT_DAYS_MASK);
      if (schedulerDirty) {
        await persistSchedulerSettings({
          enabled: schedulerEnabled,
          startTime: scheduleStartTime,
          endTime: scheduleEndTime,
          daysMask: scheduleDaysMask || DEFAULT_DAYS_MASK,
          timezone: savedScheduler.timezone || 'Asia/Kolkata',
          cancel_pending_at_end: cancelPendingAtEnd,
        });
      }
      // LIVE: gate behind an explicit confirmation before deploying. Paper deploys are unchanged.
      if (selectedExecutionMode === 'LIVE') {
        setShowDeployConfirm(true);
        return;
      }
      await runDeploy();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteButton = canDeleteStrategy ? (
    <button onClick={() => onDelete(strategy)}
      className="btn-delete flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium border border-bg-border text-bear/50 hover:text-bear hover:border-bear/30 bg-bg-hover transition-colors">
      <Trash2 size={9} /> Delete
    </button>
  ) : null;

  return (
    <>
      <div className={cn(
        'strat-card card overflow-hidden transition-all duration-200 relative border-l-2',
        isMasterStrategy
          ? isLive
            ? 'border-l-info/80 border-info/30 shadow-panel'
            : 'border-l-info/40 border-info/20 shadow-card hover:border-l-info/60 hover:border-info/30 hover:shadow-panel'
          : isLive
            ? (String(activeExecutionMode).toUpperCase() === 'PAPER'
                ? 'border-l-bg-border/60 shadow-panel'
                : 'border-l-bull/80 border-bull/30 shadow-panel')
            : 'border-l-bg-border/40 shadow-card hover:border-l-amber/50 hover:border-amber/20 hover:shadow-panel'
      )}>
        {isMasterStrategy && (
          <div className="absolute inset-0 bg-gradient-to-br from-info/10 via-info/[0.03] to-transparent pointer-events-none" />
        )}
        {!isMasterStrategy && isLive && String(activeExecutionMode).toUpperCase() !== 'PAPER' && (
          <div className="absolute inset-0 bg-gradient-to-br from-bull/5 via-transparent to-transparent pointer-events-none" />
        )}
        <div className="p-2 space-y-1.5">

          {/* Row 1: name + badge + strat id + timeframe — single line */}
          <div className="strat-head flex items-center justify-between gap-1.5">
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              <h3 className="strat-title text-xs font-bold text-txt-primary truncate">{strategy.name}</h3>
              {isMasterStrategy ? (
                isLive
                  ? <span className="strat-chip flex items-center gap-1 text-2xs font-mono text-info bg-info/10 border border-info/25 rounded px-1.5 py-0.5"><span className="strat-dot w-1.5 h-1.5 rounded-full bg-info animate-pulse" />Active</span>
                  : <span className="strat-chip text-2xs font-mono text-txt-muted bg-bg-hover border border-bg-border rounded px-1.5 py-0.5">Inactive</span>
              ) : (
                <StatusBadge lifecycleStatus={lifecycleStatus} isLive={isLive} executionMode={activeExecutionMode} />
              )}
              <span className="text-2xs font-mono text-txt-muted/70">#{strategy.id}</span>
              {sm.stratCode && <span className="text-2xs font-mono text-txt-secondary">{sm.stratCode}</span>}
            </div>
            <span className="strat-chip badge bg-transparent border border-bg-border text-txt-secondary text-2xs shrink-0">
              {sm.timeframe}
            </span>
          </div>

          {/* Mobile-only condensed summary + Details trigger (replaces Row 2 + Row 4 below lg) */}
          <div className="lg:hidden flex items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-2xs font-mono text-txt-secondary" title={configFields.map((f) => f.v).join(' · ')}>
              {configFields.map((f) => f.v).join(' · ')}
            </span>
            <button
              type="button"
              onClick={() => setShowDetails(true)}
              aria-label="View strategy details"
              className="strat-action shrink-0 flex items-center gap-1 rounded border border-bg-border text-txt-secondary hover:text-amber hover:border-amber/30 bg-bg-hover text-2xs font-mono font-medium px-2.5 py-2 -my-1"
            >
              <Info size={11} /> Details
            </button>
          </div>

          {/* Row 2: config grid — derived via mapStrategyToCardSummary (desktop only, see mobile summary above) */}
          <div className="hidden lg:grid lg:grid-cols-4 gap-1 text-2xs font-mono">
            {configFields.map(({ l, v, c }) => (
              <div key={l} className="strat-tile bg-bg-surface border border-bg-border/80 rounded px-1.5 py-1">
                <div className="text-txt-label uppercase tracking-widest mb-px" style={{fontSize:'9px'}}>{l}</div>
                <div className={cn('font-semibold truncate', c)} title={String(v)}>{v}</div>
              </div>
            ))}
          </div>

          {/* Row 4: secondary strip (desktop only, see mobile summary above) */}
          <div className="strat-strip hidden lg:flex items-center gap-2 text-2xs font-mono text-txt-secondary bg-bg-surface rounded px-2 py-0.5 border border-bg-border truncate">
            {sm.secondaryStrip ? (
              <span className="truncate" title={sm.secondaryStrip}>{sm.secondaryStrip}</span>
            ) : (
              <>
                <span>Max {sm.maxTrades ?? '—'} trades</span>
                <span>·</span>
                <span>{sm.cooldown ?? '—'}min cooldown</span>
                {sm.optSides && <><span>·</span><span>{sm.optSides}</span></>}
              </>
            )}
          </div>

          {showSchedulerControls && (
            <div className="flex items-center gap-1 text-2xs font-mono text-txt-muted">
              <span className={cn(
                'strat-chip px-1.5 py-0.5 rounded border shrink-0',
                schedulerEnabled
                  ? 'bg-info/10 border-info/30 text-info'
                  : 'bg-bg-hover border-bg-border text-txt-muted'
              )}>
                {schedulerEnabled ? 'Auto' : 'Manual'}
              </span>
              {schedulerEnabled && (
                <label className="flex items-center gap-1 text-2xs text-txt-muted cursor-pointer ml-1">
                  <input type="checkbox" checked={cancelPendingAtEnd}
                    onChange={(e) => setCancelPendingAtEnd(e.target.checked)}
                    disabled={lockScheduleInputs} className="accent-amber w-3 h-3" />
                  Cancel pending at end
                </label>
              )}
              <button
                type="button"
                onClick={handleSchedulerToggle}
                disabled={updateMut.isPending || lockScheduleInputs}
                className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ml-auto ${
                  schedulerEnabled ? 'bg-info' : 'bg-bg-border'
                }`}
                title="Toggle auto scheduler"
              >
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                  schedulerEnabled ? 'translate-x-4' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          )}

          {showSchedulerControls && schedulerEnabled && (
            <div className="w-full rounded border border-bg-border bg-bg-surface/60 px-1.5 py-1">
              <div className="flex items-center gap-1">
                <input
                  type="time"
                  value={scheduleStartTime}
                  onChange={(e) => setScheduleStartTime(e.target.value)}
                  onBlur={handleScheduleTimeBlur}
                  disabled={updateMut.isPending || lockScheduleInputs}
                  className="input-base h-7 w-[92px] text-2xs px-1.5"
                  title="Schedule start time"
                />
                <input
                  type="time"
                  value={scheduleEndTime}
                  onChange={(e) => setScheduleEndTime(e.target.value)}
                  onBlur={handleScheduleTimeBlur}
                  disabled={updateMut.isPending || lockScheduleInputs}
                  className="input-base h-7 w-[92px] text-2xs px-1.5"
                  title="Schedule end time"
                />
                {WEEKDAYS.map(({ label, bit }, i) => {
                  const on = ((scheduleDaysMask || DEFAULT_DAYS_MASK) & bit) !== 0;
                  return (
                    <button
                      key={`${label}-${i}`}
                      type="button"
                      onClick={() => toggleScheduleDay(bit)}
                      disabled={updateMut.isPending || lockScheduleInputs}
                      className={cn(
                        'h-6 w-6 shrink-0 rounded text-2xs font-mono border transition-colors',
                        on
                          ? 'bg-info/20 border-info/40 text-info'
                          : 'bg-bg-hover border-bg-border text-txt-muted hover:border-info/30'
                      )}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Row 5: action buttons */}
          <div className="strat-actions flex items-center gap-1 flex-nowrap">
            {isLive ? (
              isMasterExternal ? (
                <>
                  {masterStatus && (
                    <span className="text-2xs font-mono text-txt-muted">
                      {masterStatus.active_mapped_users}/{masterStatus.total_mapped_users} active
                    </span>
                  )}
                  <button onClick={() => setShowForceExit(true)}
                    className="strat-action flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium border border-amber/30 text-amber hover:bg-amber/10 transition-colors">
                    <LogOut size={9} /> Force Exit
                  </button>
                  <button onClick={handleMasterDeactivate}
                    disabled={deactivating}
                    className="strat-action flex items-center gap-1 bg-bear border border-bear text-white hover:bg-bear/90 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium transition-colors">
                    {deactivating ? <Spinner size={9} className="text-white" /> : <Square size={9} />}
                    Deactivate
                  </button>
                  <button onClick={() => toggleTab('signals')}
                    className={cn(
                      'strat-action flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono border transition-colors',
                      accTab === 'signals'
                        ? 'bg-amber/10 border-amber/30 text-amber'
                        : 'bg-bg-hover border-bg-border text-txt-muted hover:text-txt-primary'
                    )}>
                    <Activity size={10} />
                    <span className="hidden sm:inline">Signals</span>
                    {signals.length > 0 && (
                      <span className="bg-amber/20 text-amber rounded-full px-1 leading-none">{signals.length}</span>
                    )}
                    {accTab === 'signals' ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
                  </button>
                </>
              ) : isMasterManual ? (
                <>
                  {masterStatus && (
                    <span className="text-2xs font-mono text-txt-muted">
                      {masterStatus.active_mapped_users}/{masterStatus.total_mapped_users} active
                    </span>
                  )}
                  <button onClick={handleMasterDeactivate}
                    disabled={deactivating}
                    title="This stops the Manual Master parent only. It does not close child positions."
                    className="strat-action flex items-center gap-1 bg-bear border border-bear text-white hover:bg-bear/90 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium transition-colors">
                    {deactivating ? <Spinner size={9} className="text-white" /> : <Square size={9} />}
                    Deactivate
                  </button>
                  <button onClick={() => toggleTab('signals')}
                    className={cn(
                      'strat-action flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono border transition-colors',
                      accTab === 'signals'
                        ? 'bg-amber/10 border-amber/30 text-amber'
                        : 'bg-bg-hover border-bg-border text-txt-muted hover:text-txt-primary'
                    )}>
                    <Activity size={10} />
                    <span className="hidden sm:inline">Signals</span>
                    {signals.length > 0 && (
                      <span className="bg-amber/20 text-amber rounded-full px-1 leading-none">{signals.length}</span>
                    )}
                    {accTab === 'signals' ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
                  </button>
                  <button onClick={() => navigate(ROUTES.strategyEdit(strategy.id))}
                    className="btn-modify flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium border border-bg-border text-txt-secondary hover:text-amber hover:border-amber/30 bg-bg-hover transition-colors">
                    <Edit3 size={9} /> Modify
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setShowStop(true)}
                    className="strat-action flex items-center gap-1 bg-bear/10 border border-bear/30 text-bear hover:bg-bear/20 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-medium transition-colors">
                    <Square size={9} /> Stop
                  </button>
                  <span className="hidden lg:contents">
                    {[
                      { key: 'signals',  label: 'Signals',  I: Activity, count: signals.length },
                      { key: 'status',   label: 'Status',   I: CheckCircle, count: positionsCount + openOrdersCount },
                      { key: 'runtime',  label: 'Runtime',  I: FileText, count: null },
                    ].map(({ key, label, I, count }) => (
                      <button key={key} onClick={() => toggleTab(key)}
                        className={cn(
                          'strat-action flex items-center gap-1 rounded px-2 py-1 text-2xs border transition-colors',
                          accTab === key
                            ? 'bg-amber/10 border-amber/30 text-amber'
                            : 'bg-bg-hover border-bg-border text-txt-muted hover:text-txt-primary'
                        )}>
                        <I size={10} />
                        <span className="hidden sm:inline">{label}</span>
                        {count !== null && count > 0 && (
                          <span className="bg-amber/20 text-amber rounded-full px-1 leading-none">{count}</span>
                        )}
                        {accTab === key ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
                      </button>
                    ))}
                  </span>
                </>
              )
            ) : (
              isMasterExternal ? (
                <>
                  {masterStatus && (
                    <span className="text-2xs font-mono text-txt-muted">
                      {masterStatus.active_mapped_users}/{masterStatus.total_mapped_users} active
                    </span>
                  )}
                  <button onClick={handleMasterActivate}
                    disabled={activating}
                    className="strat-action btn-primary flex items-center gap-1 text-2xs py-2.5 px-2.5 lg:py-1 lg:px-2">
                    {activating ? <Spinner size={9} className="text-bg-base" /> : <Zap size={9} />}
                    Activate
                  </button>
                  <button onClick={() => navigate(ROUTES.strategyEdit(strategy.id))}
                    className="btn-modify flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium border border-bg-border text-txt-secondary hover:text-amber hover:border-amber/30 bg-bg-hover transition-colors">
                    <Edit3 size={9} /> Modify
                  </button>
                  {deleteButton}
                </>
              ) : isMasterManual ? (
                <>
                  {masterStatus && (
                    <span className="text-2xs font-mono text-txt-muted">
                      {masterStatus.active_mapped_users}/{masterStatus.total_mapped_users} active
                    </span>
                  )}
                  <button onClick={handleMasterActivate}
                    disabled={activating}
                    className="strat-action btn-primary flex items-center gap-1 text-2xs py-2.5 px-2.5 lg:py-1 lg:px-2">
                    {activating ? <Spinner size={9} className="text-bg-base" /> : <Zap size={9} />}
                    Activate
                  </button>
                  <button onClick={() => navigate(ROUTES.strategyEdit(strategy.id))}
                    className="btn-modify flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium border border-bg-border text-txt-secondary hover:text-amber hover:border-amber/30 bg-bg-hover transition-colors">
                    <Edit3 size={9} /> Modify
                  </button>
                  {deleteButton}
                </>
              ) : (
                <>
                  <select
                    value={selectedExecutionMode}
                    onChange={(e) => handleExecutionModeChange(e.target.value)}
                    disabled={updateMut.isPending}
                    className="input-base h-9 lg:h-7 w-20 text-2xs"
                  >
                    <option value="PAPER">Paper</option>
                    <option value="LIVE">Live</option>
                  </select>
                  <button onClick={handleDeployClick} disabled={deploying === strategy.id || updateMut.isPending}
                    className="btn-deploy flex items-center gap-1 text-2xs py-2.5 px-2.5 lg:py-1 lg:px-2">
                    {deploying === strategy.id ? <Spinner size={9} className="text-bg-base" /> : <Zap size={9} />}
                    {scheduleActionLabel}
                  </button>
                  {canStartInWindow && (
                    <button
                      onClick={handleScheduleStart}
                      disabled={resumeMut.isPending || updateMut.isPending}
                      className="strat-action flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium border border-bull/30 text-bull hover:bg-bull/10 transition-colors"
                    >
                      {resumeMut.isPending ? <Spinner size={9} /> : <Zap size={9} />}
                      Start
                    </button>
                  )}
                  <button onClick={() => navigate(ROUTES.strategyEdit(strategy.id))}
                    className="btn-modify flex items-center gap-1 rounded px-2.5 py-2.5 lg:px-2 lg:py-1 text-2xs font-mono font-medium border border-bg-border text-txt-secondary hover:text-amber hover:border-amber/30 bg-bg-hover transition-colors">
                    <Edit3 size={9} /> Modify
                  </button>
                  {deleteButton}
                </>
              )
            )}
          </div>
          {isMasterManual && isLive && (
            <div className="rounded border border-info/20 bg-info/5 px-2 py-1 text-2xs font-mono text-txt-muted">
              This stops the Manual Master parent only. It does not close child positions.
            </div>
          )}
          {notice && (
            <div className={cn(
              'rounded border px-2 py-1 text-2xs font-mono',
              notice.type === 'success' && 'bg-bull/10 border-bull/30 text-bull',
              notice.type === 'warn' && 'bg-warn/10 border-warn/30 text-warn',
              notice.type === 'info' && 'bg-info/10 border-info/30 text-info'
            )}>
              {notice.message}
            </div>
          )}
        </div>

        {/* Accordion panel (desktop only — mobile shows this inside the Details sheet instead) */}
        {accTab && isLive && (
          <div className="hidden lg:block border-t border-bg-border bg-bg-surface px-2.5 py-2">
            {accTab === 'signals'  && <SignalsPanel  depId={depId} queryParams={todayQueryParams} />}
            {!isMasterManual && accTab === 'status'  && <StatusPanel   depId={depId} strategyId={strategy.id} executionMode={activeExecutionMode} qtyMode={strategy?.config?.execution?.quantity?.mode} />}
            {!isMasterManual && accTab === 'runtime' && <RuntimePanel  depId={depId} queryParams={todayQueryParams} />}
          </div>
        )}

        {/* Footer */}
        <div className="px-2.5 py-1 border-t border-bg-border bg-bg-surface flex items-center justify-between">
          <div className="flex items-center gap-1 text-2xs text-txt-muted font-mono">
            <Clock size={9} /> Started {fmtRelative(startedAt)}
          </div>
          {lastSignalAt && (
            <div className="flex items-center gap-1 text-2xs text-txt-muted font-mono">
              <RefreshCw size={9} /> Last signal {fmtRelative(lastSignalAt)}
            </div>
          )}
        </div>
      </div>

      {!isMasterStrategy && showStop && (
        <StopModal
          name={strategy.name}
          depState={depState}
          onClose={() => setShowStop(false)}
          onStop={() => handleStop(false)}
          onExitStop={() => handleStop(true)}
          stopping={stopping}
        />
      )}
      {isMasterExternal && showForceExit && (
        <ForceExitDrawer
          strategyId={strategy.id}
          onClose={() => setShowForceExit(false)}
        />
      )}
      {isMasterStrategy && deactivateWarning && (
        <DeactivateWarningModal
          blockingChildren={deactivateWarning.blocking_children}
          isManualMaster={isMasterManual}
          onCancel={() => setDeactivateWarning(null)}
          onConfirm={handleConfirmDeactivateAnyway}
          submitting={deactivating}
        />
      )}

      <BottomSheet open={showDetails} onClose={() => setShowDetails(false)} title={strategy.name}>
        <div className="grid grid-cols-2 gap-1.5 text-2xs font-mono mb-3">
          {configFields.map(({ l, v, c }) => (
            <div key={l} className="strat-tile bg-bg-surface border border-bg-border/80 rounded px-1.5 py-1.5">
              <div className="text-txt-label uppercase tracking-widest mb-px" style={{fontSize:'9px'}}>{l}</div>
              <div className={cn('font-semibold truncate', c)} title={String(v)}>{v}</div>
            </div>
          ))}
        </div>
        <div className="strat-strip flex items-center gap-2 text-2xs font-mono text-txt-secondary bg-bg-surface rounded px-2 py-1.5 border border-bg-border mb-3">
          {sm.secondaryStrip ? (
            <span className="truncate" title={sm.secondaryStrip}>{sm.secondaryStrip}</span>
          ) : (
            <>
              <span>Max {sm.maxTrades ?? '—'} trades</span>
              <span>·</span>
              <span>{sm.cooldown ?? '—'}min cooldown</span>
              {sm.optSides && <><span>·</span><span>{sm.optSides}</span></>}
            </>
          )}
        </div>

        {!isMasterExternal && isLive && (
          <>
            <div className="flex items-center gap-1.5 mb-2">
              {(isMasterManual ? [
                { key: 'signals', label: 'Signals', I: Activity, count: signals.length },
              ] : [
                { key: 'signals', label: 'Signals', I: Activity, count: signals.length },
                { key: 'status',  label: 'Status',  I: CheckCircle, count: positionsCount + openOrdersCount },
                { key: 'runtime', label: 'Runtime', I: FileText, count: null },
              ]).map(({ key, label, I, count }) => (
                <button key={key} onClick={() => toggleTab(key)}
                  className={cn(
                    'strat-action flex-1 flex items-center justify-center gap-1 rounded px-2 py-2.5 text-2xs border transition-colors',
                    accTab === key
                      ? 'bg-amber/10 border-amber/30 text-amber'
                      : 'bg-bg-hover border-bg-border text-txt-muted hover:text-txt-primary'
                  )}>
                  <I size={11} />
                  {label}
                  {count !== null && count > 0 && (
                    <span className="bg-amber/20 text-amber rounded-full px-1 leading-none">{count}</span>
                  )}
                </button>
              ))}
            </div>
            {accTab && (
              <div className="border-t border-bg-border/60 pt-2">
                {accTab === 'signals'  && <SignalsPanel  depId={depId} queryParams={todayQueryParams} />}
                {!isMasterManual && accTab === 'status'  && <StatusPanel   depId={depId} strategyId={strategy.id} executionMode={activeExecutionMode} qtyMode={strategy?.config?.execution?.quantity?.mode} />}
                {!isMasterManual && accTab === 'runtime' && <RuntimePanel  depId={depId} queryParams={todayQueryParams} />}
              </div>
            )}
          </>
        )}
      </BottomSheet>

      <BottomSheet open={showDeployConfirm && isMobile} onClose={() => setShowDeployConfirm(false)} title="Confirm Live Deploy">
        <div className="rounded-lg border border-bear/30 bg-bear/10 text-bear text-xs px-3 py-2.5 mb-3">
          This will deploy <strong>{strategy.name}</strong> in <strong>LIVE</strong> mode. Real orders may be placed through your connected broker.
        </div>
        <div className="space-y-2 text-xs mb-4">
          <div className="flex items-center justify-between">
            <span className="text-txt-muted">Strategy</span>
            <span className="text-txt-primary font-medium">{strategy.name}</span>
          </div>
          {(sm.symbol || sm.segment) && (
            <div className="flex items-center justify-between">
              <span className="text-txt-muted">Symbol / Segment</span>
              <span className="text-txt-primary font-medium">{sm.symbol ? symbolShort(sm.symbol) : sm.segment}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-txt-muted">Execution Mode</span>
            <span className="text-bear font-semibold">LIVE</span>
          </div>
        </div>
        <div className="flex gap-3 pb-2">
          <button
            type="button"
            onClick={() => setShowDeployConfirm(false)}
            className="flex-1 min-h-[48px] rounded-md text-sm font-semibold border border-bg-border text-txt-secondary hover:bg-bg-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmLiveDeploy}
            className="flex-1 min-h-[48px] rounded-md text-sm font-semibold border border-bear/30 bg-bear/10 text-bear hover:bg-bear/20 transition-colors"
          >
            Deploy Live
          </button>
        </div>
      </BottomSheet>

      <ConfirmModal
        open={showDeployConfirm && !isMobile}
        title="Confirm Live Deploy"
        message={`This will deploy "${strategy.name}" in LIVE mode. Real orders may be placed through your connected broker.`}
        confirmLabel="Deploy Live"
        cancelLabel="Cancel"
        danger
        onConfirm={handleConfirmLiveDeploy}
        onCancel={() => setShowDeployConfirm(false)}
      />
    </>
  );
}

// ── Deactivate Warning Modal ───────────────────────────────────────────────────
function DeactivateWarningModal({ blockingChildren, isManualMaster = false, onCancel, onConfirm, submitting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-bg-card border border-bg-border rounded-xl w-full max-w-md shadow-2xl animate-fade-in">
        <div className="p-3.5 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={14} className="text-amber" />
            <h3 className="font-semibold text-txt-primary text-sm">Open positions detected</h3>
          </div>
          <p className="text-2xs text-txt-secondary leading-snug">
            {blockingChildren.length} mapped user{blockingChildren.length > 1 ? 's' : ''} currently hold open positions.
            {' '}
            {isManualMaster ? (
              <>This stops the Manual Master parent only. It does not close child positions. Each user must manage their position in Manual Trading.</>
            ) : (
              <>Deactivating will stop master signal fanout but leave these positions open at the broker. Each user must manage their position via Manual Exit, or you can use Force Exit before deactivating.</>
            )}
          </p>
          <div className="space-y-1 max-h-44 overflow-y-auto rounded border border-amber/20 bg-amber/5 p-2">
            {blockingChildren.map((c) => (
              <div key={c.child_deployment_id} className="text-2xs font-mono">
                <div className="text-txt-primary">
                  DEP:{c.child_deployment_id} · user {c.mapped_user_id}
                </div>
                <div className="pl-3 text-txt-muted">
                  {(c.open_symbols || []).map((s) => `${s.symbol} (${s.position_status})`).join(', ')}
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCancel} disabled={submitting} className="btn-ghost text-xs px-3 py-1.5">Cancel</button>
            <button onClick={onConfirm} disabled={submitting}
              className="bg-bear/10 border border-bear/30 text-bear hover:bg-bear/20 rounded text-xs px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50">
              {submitting && <Spinner size={11} />} Deactivate Anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Force Exit Drawer ──────────────────────────────────────────────────────────
function ForceExitDrawer({ strategyId, onClose }) {
  const [step, setStep]                     = useState('symbol');
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [selectedIds, setSelectedIds]       = useState([]);
  const [submitting, setSubmitting]         = useState(false);

  const { data: candidatesData, isLoading: loadingCandidates }
    = useMasterForceExitCandidates(strategyId);
  const { data: usersData, isLoading: loadingUsers }
    = useMasterForceExitUsers(strategyId, selectedSymbol);
  const forceExitMut = useMasterForceExit();

  useEffect(() => {
    if (usersData?.users) {
      setSelectedIds(
        usersData.users
          .filter((u) => u.has_open_position)
          .map((u) => u.child_deployment_id)
      );
    }
  }, [usersData]);

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await forceExitMut.mutateAsync({
        strategyId,
        payload: { child_deployment_ids: selectedIds },
      });
      onClose();
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-bg-card border border-bg-border rounded-xl w-full max-w-sm shadow-2xl">
        <div className="p-3.5 space-y-3">

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <LogOut size={13} className="text-amber" />
              <h3 className="font-semibold text-txt-primary text-sm">Force Exit</h3>
            </div>
            <button onClick={onClose} className="text-txt-muted hover:text-txt-primary">
              <X size={13} />
            </button>
          </div>

          {step === 'symbol' && (
            <>
              <p className="text-2xs text-txt-secondary font-mono">Select symbol to exit:</p>
              {loadingCandidates ? <Spinner size={14} /> : (
                <div className="space-y-1">
                  {(candidatesData?.candidates || []).map((c) => (
                    <button key={c.symbol}
                      onClick={() => { setSelectedSymbol(c.symbol); setStep('users'); }}
                      className="w-full flex items-center justify-between rounded px-2 py-2 text-2xs font-mono border border-bg-border hover:border-amber/30 hover:bg-amber/5 transition-colors">
                      <span className="text-txt-primary">{c.symbol}</span>
                      <span className="text-txt-muted">{c.active_user_count} users · {c.last_signal}</span>
                    </button>
                  ))}
                  {!candidatesData?.candidates?.length && (
                    <p className="text-2xs text-txt-muted text-center py-2">No open positions found</p>
                  )}
                </div>
              )}
            </>
          )}

          {step === 'users' && (
            <>
              <div className="flex items-center gap-2">
                <button onClick={() => setStep('symbol')} className="text-txt-muted hover:text-txt-primary">
                  <ArrowLeft size={11} />
                </button>
                <p className="text-2xs font-mono text-txt-secondary truncate">{selectedSymbol}</p>
              </div>
              {loadingUsers ? <Spinner size={14} /> : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {(usersData?.users || []).map((u) => (
                    <label key={u.child_deployment_id}
                      className="flex items-center gap-2 rounded px-2 py-1.5 text-2xs font-mono border border-bg-border cursor-pointer hover:bg-bg-hover">
                      <input type="checkbox"
                        checked={selectedIds.includes(u.child_deployment_id)}
                        onChange={(e) => setSelectedIds(p =>
                          e.target.checked ? [...p, u.child_deployment_id]
                                           : p.filter(id => id !== u.child_deployment_id)
                        )}
                        className="accent-amber"
                      />
                      <span className="flex-1 text-txt-primary">DEP:{u.child_deployment_id}</span>
                      {u.has_open_position && (
                        <span className={cn(
                          'rounded-full px-1.5 py-0.5 text-[9px] font-medium',
                          u.position_status === 'LONG' ? 'bg-bull/10 text-bull' : 'bg-bear/10 text-bear'
                        )}>
                          {u.position_status}
                        </span>
                      )}
                      <span className="text-txt-muted">{u.execution_mode}</span>
                    </label>
                  ))}
                </div>
              )}
              <button
                onClick={() => setStep('confirm')}
                disabled={selectedIds.length === 0}
                className="w-full btn-primary text-xs py-2 disabled:opacity-50">
                Continue ({selectedIds.length} selected)
              </button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <div className="text-2xs font-mono text-amber bg-amber/5 border border-amber/20 rounded px-2 py-1.5">
                ⚠ Exit {selectedIds.length} user{selectedIds.length > 1 ? 's' : ''} for {selectedSymbol}? Deployments stay active.
              </div>
              <div className="text-2xs font-mono text-txt-muted bg-bg-hover/40 border border-bg-border rounded px-2 py-1.5 leading-snug">
                Tip: new entry signals can still arrive while this exit runs.
                To halt all entries first, deactivate the master.
              </div>
              <div className="space-y-2 pt-1">
                <button onClick={handleConfirm} disabled={submitting}
                  className="w-full btn-primary text-xs py-2.5 flex items-center justify-center gap-2">
                  {submitting ? <Spinner size={12} /> : <LogOut size={12} />}
                  Force Exit Selected Users
                </button>
                <button onClick={() => setStep('users')} className="w-full btn-ghost text-xs py-2">
                  Back
                </button>
              </div>
            </>
          )}

          <button onClick={onClose} className="w-full btn-ghost text-xs py-1.5">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Mapped Master Strategy Card ────────────────────────────────────────────────
// Rendered for operator-assigned External or Manual master strategies (read-only core;
// execution overrides editable via detail page). No Deploy/Delete/Edit buttons.
const QTY_INPUT_LABELS = {
  FIXED_QTY:  'Fixed Qty',
  BY_ALERT:   'Qty Multiplier',
  MULTIPLIER: 'Qty Multiplier',
  BY_MARGIN:  'Margin (₹)',
  BY_RISK:    'Risk',
};

function MappedStrategyCard({ mapped, brokerStatus, onTokenGateRequest }) {
  const sm = mapMappedStrategyToCardSummary(mapped);
  const meta = sm.mappedMeta;
  const isMobile = useIsMobile();
  const isMappedManual = meta.masterStrategyMode === STRATEGY_MODES.MASTER_MANUAL;
  const canSelectManualLive = isMappedManual && meta.manualLiveSelectionAvailable === true;
  const canShowExecutionModeSelector = !isMappedManual || canSelectManualLive;

  const mappingStatus = String(meta.status || 'UNKNOWN').toUpperCase();
  const childStatus = String(meta.childDeploymentStatus || 'UNKNOWN').toUpperCase();
  const isLive = childStatus === 'ACTIVE';
  const activeExecutionMode = normalizeExecutionMode(
    isMappedManual && !canSelectManualLive ? 'PAPER' : meta.executionMode
  );
  const depId = meta.childDeploymentId ?? null;
  const mappingId = meta.mappingId ?? null;
  const qtyMode = String(meta.qtyMode || '').toUpperCase() || 'FIXED_QTY';
  const qtyLabel = QTY_INPUT_LABELS[qtyMode] ?? 'Qty Multiplier';

  const [accTab, setAccTab] = useState(null);
  const [selectedExecutionMode, setSelectedExecutionMode] = useState(
    isMappedManual && !canSelectManualLive ? 'PAPER' : activeExecutionMode
  );
  const [qtyInput, setQtyInput] = useState(meta.qtyValue ?? '');
  // qty_value must be a positive integer >= 1 across every mapping mode.
  // Deploy is blocked until the input parses to an integer >= 1.
  const qtyInputValid = (() => {
    const trimmed = String(qtyInput ?? '').trim();
    if (trimmed === '') return false;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) && Number.isInteger(parsed) && parsed >= 1;
  })();
  const [selfEditSupported, setSelfEditSupported] = useState(true);
  const [notice, setNotice] = useState(null);
  const [showStop, setShowStop] = useState(false);
  const [mappedLiveConfirm, setMappedLiveConfirm] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [schedulerOn, setSchedulerOn] = useState(!!meta.schedulerEnabled);
  const [scheduleStart, setScheduleStart] = useState(meta.startTime || '');
  const [scheduleEnd, setScheduleEnd] = useState(meta.endTime || '');
  const [scheduleDays, setScheduleDays] = useState(
    Number.isInteger(meta.daysMask) ? meta.daysMask : DEFAULT_DAYS_MASK
  );

  const updateMut = useUpdateMappedStrategy(mappingId);
  const stopMut   = useStopDeployment();
  const exitStop  = useExitAndStop();

  const { data: depState } = useDeploymentState(isMappedManual ? null : depId);

  useEffect(() => {
    setSelectedExecutionMode(
      isMappedManual && !canSelectManualLive ? 'PAPER' : normalizeExecutionMode(meta.executionMode)
    );
  }, [isMappedManual, canSelectManualLive, meta.executionMode]);

  useEffect(() => {
    setQtyInput(meta.qtyValue ?? '');
  }, [meta.qtyValue]);

  useEffect(() => {
    setSchedulerOn(!!meta.schedulerEnabled);
    setScheduleStart(meta.startTime || '');
    setScheduleEnd(meta.endTime || '');
    setScheduleDays(Number.isInteger(meta.daysMask) ? meta.daysMask : DEFAULT_DAYS_MASK);
  }, [meta.schedulerEnabled, meta.startTime, meta.endTime, meta.daysMask]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 3500);
    return () => clearTimeout(t);
  }, [notice]);

  const showInlineNotice = (message, type = 'info') => setNotice({ message, type, ts: Date.now() });

  const todayIst = getTodayIstDate();
  const todayQueryParams = useMemo(() => ({ from_date: todayIst, to_date: todayIst }), [todayIst]);

  const { data: symbolStatus } = useDeploymentSymbolStatus(isMappedManual ? null : depId);
  const { data: signalsData } = useSignals(depId, todayQueryParams);

  const signals = signalsData?.signals || signalsData || [];
  const symbolRows = symbolStatus?.rows || [];
  const openOrdersCount = symbolStatus?.open_orders_count ?? 0;
  const positionsCount = symbolStatus?.positions_count ?? symbolRows.filter((row) => (
    ['LONG', 'SHORT'].includes(String(row.position_status || '').toUpperCase())
  )).length;

  const toggleTab = (tab) => setAccTab((p) => (p === tab ? null : tab));

  const handlePatch = async (payload, { optimistic, onRollback, successMessage } = {}) => {
    if (!mappingId) return;
    if (optimistic) optimistic();
    try {
      await updateMut.mutateAsync(payload);
      if (successMessage) showInlineNotice(successMessage, 'success');
    } catch (err) {
      const st = err?.status;
      if (st === 404 || st === 405 || st === 501) {
        setSelfEditSupported(false);
        if (onRollback) onRollback();
        showInlineNotice('Operator-managed — editing disabled', 'warn');
        return;
      }
      if (onRollback) onRollback();
      toast.error(err?.message || 'Update failed');
    }
  };

  const handleExecutionModeChange = (nextMode) => {
    const normalized = normalizeExecutionMode(nextMode);
    const prev = selectedExecutionMode;
    handlePatch(
      { execution_mode: normalized },
      {
        optimistic: () => setSelectedExecutionMode(normalized),
        onRollback: () => setSelectedExecutionMode(prev),
      },
    );
  };

  const handleQtyBlur = () => {
    const trimmed = String(qtyInput).trim();
    if (trimmed === '') {
      setQtyInput(meta.qtyValue ?? '');
      return;
    }
    const parsed = Number(trimmed);
    // Reject non-integer / decimal / non-positive values; revert input.
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 1) {
      setQtyInput(meta.qtyValue ?? '');
      toast.error('Qty must be a positive integer ≥ 1');
      return;
    }
    const current = Number(meta.qtyValue ?? NaN);
    if (Number.isFinite(current) && parsed === current) return;
    handlePatch(
      { qty_value: parsed },
      { onRollback: () => setQtyInput(meta.qtyValue ?? '') },
    );
  };

  const handleConfirmMappedLiveDeploy = () => {
    const runConfirmedDeploy = mappedLiveConfirm;
    setMappedLiveConfirm(null);
    if (typeof runConfirmedDeploy === 'function') runConfirmedDeploy();
  };

  const handleCancelMappedLiveDeploy = () => setMappedLiveConfirm(null);

  const handleDeployClick = () => {
    if (!qtyInputValid) {
      toast.error('Qty must be a positive integer ≥ 1');
      return;
    }
    const effectiveExecutionMode = isMappedManual && !canSelectManualLive
      ? 'PAPER'
      : selectedExecutionMode;
    const payload = { status: 'ACTIVE', execution_mode: effectiveExecutionMode };
    const parsed = Number(qtyInput);
    if (Number.isFinite(parsed) && Number.isInteger(parsed) && parsed >= 1) payload.qty_value = parsed;
    const runPatch = () => handlePatch(payload, { successMessage: 'Mapping activated' });
    if (effectiveExecutionMode === 'LIVE') {
      if (!brokerStatus?.has_active_broker) {
        toast.error('No active broker mapped. Please configure a broker before deploying in LIVE mode.');
        return;
      }
      const tokenStatus = brokerStatus?.token_status;
      // Skip token gate for scheduled mappings — auto-token refreshes at start time.
      if (tokenStatus && tokenStatus !== 'ACTIVE' && tokenStatus !== 'NO_BROKER' && !schedulerOn) {
        onTokenGateRequest?.({ resume: runPatch });
        return;
      }
      setMappedLiveConfirm(() => runPatch);
      return;
    }
    runPatch();
  };

  const handleSchedulerToggle = () => {
    const next = !schedulerOn;
    const prev = schedulerOn;
    handlePatch(
      { scheduler_enabled: next },
      {
        optimistic: () => setSchedulerOn(next),
        onRollback: () => setSchedulerOn(prev),
      },
    );
  };

  const handleScheduleTimeBlur = () => {
    if (!schedulerOn) return;
    if (scheduleStart === (meta.startTime || '') && scheduleEnd === (meta.endTime || '')) return;
    handlePatch({
      schedule_start_time: scheduleStart || null,
      schedule_end_time: scheduleEnd || null,
    });
  };

  const toggleScheduleDay = (bit) => {
    const next = (scheduleDays || DEFAULT_DAYS_MASK) ^ bit;
    const prev = scheduleDays;
    handlePatch(
      { schedule_days_mask: next },
      {
        optimistic: () => setScheduleDays(next),
        onRollback: () => setScheduleDays(prev),
      },
    );
  };

  const handleStop = async (withExit) => {
    if (!depId || (withExit && isMappedManual)) return;
    setStopping(true);
    try {
      if (withExit) await exitStop.mutateAsync(depId);
      else          await stopMut.mutateAsync(depId);
      showInlineNotice(`"${meta.masterStrategyName}" stopped`, 'success');
      setShowStop(false);
    } catch (e) {
      toast.error(e?.message || 'Stop failed');
    }
    setStopping(false);
  };

  const editingLocked = !selfEditSupported || !mappingId || mappingStatus === 'REMOVED';

  return (
    <>
      <div className={cn(
        'strat-card card overflow-hidden transition-all duration-200 relative border-l-2',
        isLive
          ? (String(activeExecutionMode).toUpperCase() === 'PAPER'
              ? 'border-l-bg-border/60 shadow-panel'
              : 'border-l-bull/80 border-bull/30 shadow-panel')
          : 'border-l-bg-border/40 shadow-card hover:border-l-amber/50 hover:border-amber/20 hover:shadow-panel'
      )}>
        {isLive && String(activeExecutionMode).toUpperCase() !== 'PAPER' && (
          <div className="absolute inset-0 bg-gradient-to-br from-bull/5 via-transparent to-transparent pointer-events-none" />
        )}
        <div className="p-2 space-y-1.5">

          {/* Row 1 — name + status + id + Mapped pill */}
          <div className="strat-head flex items-center justify-between gap-1.5">
            <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
              <h3 className="strat-title text-xs font-bold text-txt-primary truncate">{meta.masterStrategyName}</h3>
              <StatusBadge lifecycleStatus={childStatus} isLive={isLive} executionMode={activeExecutionMode} />
              {mappingId != null && (
                <span className="text-2xs font-mono text-txt-muted/70">#{mappingId}</span>
              )}
              <span className="strat-chip text-2xs font-mono text-info bg-info/10 border border-info/25 rounded px-1.5 py-0.5">
                Mapped
              </span>
              {mappingStatus && mappingStatus !== 'ACTIVE' && (
                <span className={cn('strat-chip text-2xs font-mono rounded px-1.5 py-0.5', mappingStatusBadgeClass(mappingStatus))}>
                  {mappingStatus}
                </span>
              )}
            </div>
            <span className="strat-chip badge bg-transparent border border-bg-border text-txt-secondary text-2xs shrink-0">
              {isMappedManual ? 'Manual' : 'External'}
            </span>
          </div>

          {/* Subtitle */}
          <div className="text-2xs font-mono text-txt-muted">
            Assigned by <span className="text-txt-secondary">{meta.operatorName}</span>
          </div>

          {/* Row 2 — 4-tile config grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 text-2xs font-mono">
            {(() => {
              if (isMappedManual) {
                return [
                  { l: 'Type',       v: 'Manual',                    c: 'text-info' },
                  { l: 'Mode',       v: activeExecutionMode === 'LIVE' ? 'Live' : 'Paper', c: 'text-txt-primary' },
                  { l: qtyLabel,     v: meta.qtyValue ?? '—',        c: 'text-txt-primary' },
                  { l: 'Deployment', v: childStatus || 'UNKNOWN',    c: isLive ? 'text-bull' : 'text-txt-muted' },
                ].map(({ l, v, c }) => (
                  <div key={l} className="strat-tile bg-bg-surface border border-bg-border/80 rounded px-1.5 py-1">
                    <div className="text-txt-label uppercase tracking-widest mb-px" style={{fontSize:'9px'}}>{l}</div>
                    <div className={cn('font-semibold truncate', c)} title={String(v)}>{String(v)}</div>
                  </div>
                ));
              }
              const hasLong  = symbolRows.some((row) => String(row.position_status || '').toUpperCase() === 'LONG');
              const hasShort = symbolRows.some((row) => String(row.position_status || '').toUpperCase() === 'SHORT');
              const positionsColor = hasLong && hasShort ? 'text-amber' : hasLong ? 'text-bull' : hasShort ? 'text-bear' : 'text-txt-muted';
              const fields = [
                { l: 'Segment',   v: sm.segment || 'External',                     c: 'text-txt-primary' },
                { l: 'Rule',      v: sm.ruleLabel || 'Operator',                   c: 'text-txt-primary' },
                { l: 'Orders',    v: openOrdersCount,                              c: openOrdersCount > 0 ? 'text-amber' : 'text-txt-primary' },
                { l: 'Positions', v: positionsCount,                               c: positionsColor },
              ];
              return fields.map(({ l, v, c }) => (
                <div key={l} className="strat-tile bg-bg-surface border border-bg-border/80 rounded px-1.5 py-1">
                  <div className="text-txt-label uppercase tracking-widest mb-px" style={{fontSize:'9px'}}>{l}</div>
                  <div className={cn('font-semibold truncate', c)} title={String(v)}>{String(v)}</div>
                </div>
              ));
            })()}
          </div>

          {/* Secondary strip */}
          <div className="flex items-center gap-2 text-2xs font-mono text-txt-secondary bg-bg-surface rounded px-2 py-0.5 border border-bg-border truncate">
            {sm.secondaryStrip ? (
              <span className="truncate" title={sm.secondaryStrip}>{sm.secondaryStrip}</span>
            ) : (
              <span className="truncate">Operator-managed · no overrides</span>
            )}
          </div>

          {/* Per-user scheduler. Signals outside the window are dropped for this user only. */}
          {!isMappedManual && !isLive && !editingLocked && (
            <>
              <div className="flex items-center gap-1 text-2xs font-mono text-txt-muted">
                <span className={cn(
                  'strat-chip px-1.5 py-0.5 rounded border shrink-0',
                  schedulerOn
                    ? 'bg-info/10 border-info/30 text-info'
                    : 'bg-bg-hover border-bg-border text-txt-muted'
                )}>
                  {schedulerOn ? 'Auto' : 'Manual'}
                </span>
                <button
                  type="button"
                  onClick={handleSchedulerToggle}
                  disabled={updateMut.isPending}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ml-auto ${
                    schedulerOn ? 'bg-info' : 'bg-bg-border'
                  }`}
                  title="Toggle auto scheduler"
                >
                  <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                    schedulerOn ? 'translate-x-4' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
              {schedulerOn && (
                <div className="w-full rounded border border-bg-border bg-bg-surface/60 px-1.5 py-1">
                  <div className="flex items-center gap-1">
                    <input
                      type="time"
                      value={scheduleStart}
                      onChange={(e) => setScheduleStart(e.target.value)}
                      onBlur={handleScheduleTimeBlur}
                      disabled={updateMut.isPending}
                      className="input-base h-7 w-[92px] text-2xs px-1.5"
                      title="Schedule start time"
                    />
                    <input
                      type="time"
                      value={scheduleEnd}
                      onChange={(e) => setScheduleEnd(e.target.value)}
                      onBlur={handleScheduleTimeBlur}
                      disabled={updateMut.isPending}
                      className="input-base h-7 w-[92px] text-2xs px-1.5"
                      title="Schedule end time"
                    />
                    {WEEKDAYS.map(({ label, bit }, i) => {
                      const on = ((scheduleDays || DEFAULT_DAYS_MASK) & bit) !== 0;
                      return (
                        <button
                          key={`${label}-${i}`}
                          type="button"
                          onClick={() => toggleScheduleDay(bit)}
                          disabled={updateMut.isPending}
                          className={cn(
                            'h-6 w-6 shrink-0 rounded text-2xs font-mono border transition-colors',
                            on
                              ? 'bg-info/20 border-info/40 text-info'
                              : 'bg-bg-hover border-bg-border text-txt-muted hover:border-info/30'
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Action row */}
          <div className="strat-actions flex items-center gap-1 flex-nowrap">
            {isLive ? (
              <>
                <button
                  onClick={() => setShowStop(true)}
                  disabled={!depId}
                  className="strat-action flex items-center gap-1 bg-bear/10 border border-bear/30 text-bear hover:bg-bear/20 rounded px-2 py-1 text-2xs font-medium transition-colors disabled:opacity-50"
                >
                  <Square size={9} /> Stop
                </button>
                {meta.qtyValue != null && (
                  <span
                    className="flex items-center gap-1 rounded px-1.5 py-1 text-2xs font-mono border bg-bg-hover border-bg-border text-txt-secondary shrink-0"
                    title={`${qtyLabel} (locked while live)`}
                  >
                    <span className="text-txt-label uppercase tracking-widest" style={{fontSize:'9px'}}>{qtyLabel}</span>
                    <span className="font-semibold text-txt-primary">{meta.qtyValue}</span>
                  </span>
                )}
                {(isMappedManual ? [
                  { key: 'signals', label: 'Signals', I: Activity, count: signals.length },
                ] : [
                  { key: 'signals',  label: 'Signals',  I: Activity,    count: signals.length },
                  { key: 'status',   label: 'Status',   I: CheckCircle, count: positionsCount + openOrdersCount },
                  { key: 'runtime',  label: 'Runtime',  I: FileText,    count: null },
                ]).map(({ key, label, I, count }) => (
                  <button
                    key={key}
                    onClick={() => toggleTab(key)}
                    className={cn(
                      'strat-action flex items-center gap-1 rounded px-2 py-1 text-2xs border transition-colors',
                      accTab === key
                        ? 'bg-amber/10 border-amber/30 text-amber'
                        : 'bg-bg-hover border-bg-border text-txt-muted hover:text-txt-primary'
                    )}
                  >
                    <I size={10} />
                    <span className="hidden sm:inline">{label}</span>
                    {count !== null && count > 0 && (
                      <span className="bg-amber/20 text-amber rounded-full px-1 leading-none">{count}</span>
                    )}
                    {accTab === key ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
                  </button>
                ))}
              </>
            ) : (
              <>
                {!canShowExecutionModeSelector ? (
                  <span className="input-base h-7 w-20 text-2xs inline-flex items-center">Paper</span>
                ) : (
                  <select
                    value={selectedExecutionMode}
                    onChange={(e) => handleExecutionModeChange(e.target.value)}
                    disabled={updateMut.isPending || editingLocked}
                    className="input-base h-7 w-20 text-2xs"
                  >
                    <option value="PAPER">Paper</option>
                    <option value="LIVE">Live</option>
                  </select>
                )}
                <label className="flex items-center gap-1 text-2xs font-mono text-txt-muted">
                  <span className="text-txt-label uppercase tracking-widest" style={{fontSize:'9px'}}>{qtyLabel}</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={qtyInput}
                    onChange={(e) => setQtyInput(e.target.value)}
                    onBlur={handleQtyBlur}
                    disabled={updateMut.isPending || editingLocked}
                    className={cn(
                      "input-base h-7 w-20 text-2xs",
                      !qtyInputValid && "border-bear/50",
                    )}
                    title={qtyLabel + ' — positive integer ≥ 1'}
                  />
                </label>
                <button
                  onClick={handleDeployClick}
                  disabled={updateMut.isPending || editingLocked || !qtyInputValid}
                  title={!qtyInputValid ? 'Qty must be a positive integer ≥ 1' : 'Deploy'}
                  className="btn-deploy flex items-center gap-1 text-2xs py-1 px-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateMut.isPending ? <Spinner size={9} className="text-bg-base" /> : <Zap size={9} />}
                  Deploy
                </button>
              </>
            )}
          </div>

          {notice && (
            <div className={cn(
              'rounded border px-2 py-1 text-2xs font-mono',
              notice.type === 'success' && 'bg-bull/10 border-bull/30 text-bull',
              notice.type === 'warn' && 'bg-warn/10 border-warn/30 text-warn',
              notice.type === 'info' && 'bg-info/10 border-info/30 text-info'
            )}>
              {notice.message}
            </div>
          )}
        </div>

        {/* Accordion */}
        {accTab && isLive && depId && (
          <div className="border-t border-bg-border bg-bg-surface px-2.5 py-2">
            {accTab === 'signals' && <SignalsPanel depId={depId} queryParams={todayQueryParams} />}
            {!isMappedManual && accTab === 'status'  && <StatusPanel  depId={depId} strategyId={meta.masterStrategyId} executionMode={activeExecutionMode} qtyMode={meta?.qtyMode || mapped?.qty_mode} />}
            {!isMappedManual && accTab === 'runtime' && <RuntimePanel depId={depId} queryParams={todayQueryParams} />}
          </div>
        )}
      </div>

      {showStop && (
        <StopModal
          name={meta.masterStrategyName}
          depState={isMappedManual ? null : depState}
          onClose={() => setShowStop(false)}
          onStop={() => handleStop(false)}
          onExitStop={() => handleStop(true)}
          stopping={stopping}
          allowExitStop={!isMappedManual}
          description={isMappedManual ? (
            <>Stopping this mapped Manual strategy will stop future copied orders for this child account. It will not stop the master strategy or close child positions.</>
          ) : null}
        />
      )}

      <BottomSheet open={!!mappedLiveConfirm && isMobile} onClose={handleCancelMappedLiveDeploy} title="Confirm Live Deploy">
        <div className="rounded-lg border border-bear/30 bg-bear/10 text-bear text-xs px-3 py-2.5 mb-3">
          {isMappedManual ? 'Manual Master LIVE copy is enabled only for normal-symbol entries and exits. Dynamic Option LIVE is not supported yet. Live orders may be sent to your broker account.' : <>This will deploy mapped strategy <strong>{meta.masterStrategyName || 'Mapped strategy'}</strong> in <strong>LIVE</strong> mode. Real orders may be placed through your connected broker.</>}
        </div>
        <div className="space-y-2 text-xs mb-4">
          <div className="flex items-center justify-between gap-3">
            <span className="text-txt-muted">Strategy</span>
            <span className="text-txt-primary font-medium truncate text-right">{meta.masterStrategyName || 'Mapped strategy'}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-txt-muted">Strategy Type</span>
            <span className="text-txt-primary font-medium">{sm.segment || 'Mapped Strategy'}</span>
          </div>
          {sm.secondaryStrip && (
            <div className="flex items-center justify-between gap-3">
              <span className="text-txt-muted">Details</span>
              <span className="text-txt-primary font-medium truncate text-right">{sm.secondaryStrip}</span>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-txt-muted">Execution Mode</span>
            <span className="text-bear font-semibold">LIVE</span>
          </div>
        </div>
        <div className="flex gap-3 pb-2">
          <button
            type="button"
            onClick={handleCancelMappedLiveDeploy}
            className="flex-1 min-h-[48px] rounded-md text-sm font-semibold border border-bg-border text-txt-secondary hover:bg-bg-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmMappedLiveDeploy}
            className="flex-1 min-h-[48px] rounded-md text-sm font-semibold border border-bear/30 bg-bear/10 text-bear hover:bg-bear/20 transition-colors"
          >
            Deploy Live
          </button>
        </div>
      </BottomSheet>

      <ConfirmModal
        open={!!mappedLiveConfirm && !isMobile}
        title="Confirm Live Deploy"
        message={isMappedManual ? 'Manual Master LIVE copy is enabled only for normal-symbol entries and exits. Dynamic Option LIVE is not supported yet. Live orders may be sent to your broker account.' : `This will deploy mapped strategy "${meta.masterStrategyName || 'Strategy'}" in LIVE mode. Real orders may be placed through your connected broker.`}
        confirmLabel="Deploy Live"
        cancelLabel="Cancel"
        danger
        onConfirm={handleConfirmMappedLiveDeploy}
        onCancel={handleCancelMappedLiveDeploy}
      />
    </>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function StrategyCardSkeleton() {
  return (
    <div className="card overflow-hidden animate-pulse">
      <div className="p-3 space-y-2.5">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1.5 flex-1">
            <div className="h-4 bg-bg-border/60 rounded w-2/3" />
            <div className="h-2.5 bg-bg-border/40 rounded w-1/3" />
          </div>
          <div className="h-5 w-12 bg-bg-border/40 rounded" />
        </div>
        <div className="grid grid-cols-4 gap-2">
          {[0,1,2,3].map((i) => (
            <div key={i} className="space-y-1">
              <div className="h-2 bg-bg-border/40 rounded w-3/4" />
              <div className="h-4 bg-bg-border/60 rounded" />
            </div>
          ))}
        </div>
        <div className="h-6 bg-bg-border/40 rounded w-full" />
        <div className="flex gap-1.5">
          <div className="h-7 w-16 bg-bg-border/50 rounded" />
          <div className="h-7 w-16 bg-bg-border/40 rounded" />
          <div className="h-7 w-10 bg-bg-border/40 rounded" />
        </div>
      </div>
      <div className="px-2.5 py-1 border-t border-bg-border">
        <div className="h-2.5 bg-bg-border/40 rounded w-24" />
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────
export default function StrategiesPage() {
  const navigate  = useNavigate();
  const deleteMut = useDeleteStrategy();
  const deployMut = useDeployStrategy();
  const exitAndStartMut = useExitAndStartDeployment();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filter, setFilter] = useState('All');
  const [tokenGatePending, setTokenGatePending] = useState(null);
  const [modeSwitchBlock, setModeSwitchBlock] = useState(null);

  const { data: strategies, isLoading, error, refetch } = useStrategies();
  const {
    data: mappedStrategies,
    isLoading: mappedLoading,
    error: mappedError,
    refetch: refetchMapped,
  } = useMappedStrategies();
  const { data: brokerStatus } = useBrokerCurrent();

  // Unified, discriminated list: owned strategies + External/Manual master mappings.
  const merged = useMemo(() => {
    const std = (strategies || []).map((s) => ({ _kind: 'standard', data: s }));
    const mapped = (mappedStrategies || []).map((m) => ({ _kind: 'mapped', data: m }));
    return [...std, ...mapped];
  }, [strategies, mappedStrategies]);

  const isItemLive = (item) => {
    if (item._kind === 'standard') return !!item.data.active_deployment_id;
    const status = String(item.data?.child_deployment_status ?? '').toUpperCase();
    return status === 'ACTIVE';
  };

  const liveCount = merged.filter(isItemLive).length;

  // The account is only "empty" once both sources have loaded successfully.
  // Owned strategies render as soon as they arrive (mapped cards follow), but
  // with none yet, a pending or failed mapped list must not read as empty.
  const showSkeleton = isLoading || (!merged.length && mappedLoading);
  const showMappedError = !merged.length && !!mappedError;
  const showNoStrategies = !error && !showSkeleton && !showMappedError && !merged.length;

  const filtered = useMemo(() => {
    if (filter === 'Running')     return merged.filter(isItemLive);
    if (filter === 'Not Running') return merged.filter((it) => !isItemLive(it));
    return merged;
  }, [merged, filter]);

  const handleDeploy = async (strategy, executionMode, schedulerState) => {
    try {
      const mode = normalizeExecutionMode(executionMode);
      if (mode === 'LIVE' && !brokerStatus?.has_active_broker) {
        toast.error('No active broker mapped. Please configure a broker before deploying in LIVE mode.');
        return;
      }
      const tokenStatus = brokerStatus?.token_status;
      const sc = schedulerState || deriveSchedulerSettings(strategy);
      // Skip token gate for scheduled strategies — auto-token generates it at start time
      if (mode === 'LIVE' && tokenStatus && tokenStatus !== 'ACTIVE' && tokenStatus !== 'NO_BROKER' && !sc.enabled) {
        setTokenGatePending({ strategy, executionMode, schedulerState });
        return;
      }
      const payload = {
        execution_mode: mode,
        scheduler_enabled: !!sc.enabled,
      };
      if (sc.enabled) {
        if (!sc.startTime || !sc.endTime) {
          throw new Error('Start and end time are required when Auto Scheduler is enabled.');
        }
        payload.schedule_start_time = sc.startTime;
        payload.schedule_end_time = sc.endTime;
        payload.schedule_days_mask = sc.daysMask || DEFAULT_DAYS_MASK;
        payload.schedule_timezone = sc.timezone || 'Asia/Kolkata';
        payload.cancel_pending_at_end = !!sc.cancel_pending_at_end;
      }
      return await deployMut.mutateAsync({ id: strategy.id, payload });
    } catch (err) {
      if (err?.status === 409 && err?.data?.error_code === 'MODE_SWITCH_BLOCKED') {
        setModeSwitchBlock({
          ...err.data,
          strategy_name: strategy.name,
          deployment_id: strategy.latest_deployment_id || strategy.active_deployment_id,
        });
        return;
      }
      if (err?.status === 409) {
        toast.error(err?.data?.detail || 'Scheduling conflict — please choose a different start time.');
        return;
      }
      toast.error(err.message);
    }
  };

  const handleExitAndStart = async () => {
    const targetMode = modeSwitchBlock?.requested_mode;
    const deploymentId = modeSwitchBlock?.deployment_id;
    if (!targetMode || !deploymentId) return;
    try {
      const result = await exitAndStartMut.mutateAsync({ id: deploymentId, targetMode });
      if (result?.started) {
        toast.success(`Exited ${result.exited_positions?.length || 0} position(s). Started in ${targetMode}.`);
        setModeSwitchBlock(null);
      } else {
        const failed = result?.failed_positions || [];
        toast.error(`Remediation failed for ${failed.length} position(s): ${failed.map((f) => f.symbol).join(', ')}`);
        setModeSwitchBlock(null);
      }
    } catch (err) {
      toast.error(err.message || 'Exit and start failed');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleteMut.isPending) return;

    const target = deleteTarget;

    try {
      await deleteMut.mutateAsync(target.id);
      setDeleteTarget(null);
      toast.success(`"${target.name}" deleted`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div className="space-y-3">

      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3 pb-3 border-b border-bg-border/50">
        <div>
          <h1 className="text-base md:text-lg font-semibold text-txt-primary">Strategies</h1>
          <p className="text-2xs text-txt-muted font-mono mt-0.5">
            {merged.length} total · {liveCount} live
          </p>
        </div>
        {/* The empty state carries its own Create Strategy button. */}
        {!showNoStrategies && (
          <button onClick={() => navigate(ROUTES.STRATEGIES_CREATE)}
            className="btn-primary flex items-center gap-1.5 text-xs w-full sm:w-auto justify-center sm:justify-start">
            <Plus size={12} /> New Strategy
          </button>
        )}
      </div>

      {/* Filter tabs */}
      {!!merged.length && (
        <div className="flex gap-1 bg-bg-surface border border-bg-border rounded-lg p-1 w-full sm:w-fit overflow-x-auto">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={cn(
                'px-3 py-1 rounded text-2xs font-mono font-medium transition-colors',
                filter === f
                  ? 'bg-amber/15 text-amber border border-amber/25'
                  : 'text-txt-secondary hover:text-txt-primary'
              )}>
              {f}
              {f === 'Running' && liveCount > 0 && (
                <span className="ml-1 bg-bull/20 text-bull rounded-full px-1">{liveCount}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Main content */}
      {error ? (
        <ErrorState message={error.message} onRetry={refetch} />
      ) : showSkeleton ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
          {[0,1,2,3].map((i) => <StrategyCardSkeleton key={i} />)}
        </div>
      ) : showMappedError ? (
        <ErrorState message={mappedError.message} onRetry={refetchMapped} />
      ) : showNoStrategies ? (
        <div className="panel p-10">
          <EmptyState
            title="No strategies yet"
            description="Create your first strategy to get started."
            action={
              <button onClick={() => navigate(ROUTES.STRATEGIES_CREATE)}
                className="btn-primary mt-4 flex items-center gap-1.5">
                <Plus size={13} /> Create Strategy
              </button>
            }
          />
        </div>
      ) : filtered.length === 0 ? (
        <div className="panel p-8">
          <EmptyState
            title={`No ${filter.toLowerCase()} strategies`}
            description="Try a different filter, or create a new strategy."
            action={
              <button onClick={() => setFilter('All')} className="btn-secondary mt-4 text-xs">
                Show all
              </button>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3">
          {filtered.map((item) => {
            if (item._kind === 'mapped') {
              const m = item.data;
              return (
                <MappedStrategyCard
                  key={`mapped-${m.mapping_id ?? m.id ?? JSON.stringify(m)}`}
                  mapped={m}
                  brokerStatus={brokerStatus}
                  onTokenGateRequest={(req) => setTokenGatePending(req)}
                />
              );
            }
            const s = item.data;
            return (
              <StrategyCard
                key={`standard-${s.id}`}
                strategy={s}
                deploying={deployMut.isPending ? deployMut.variables?.id : null}
                onDeploy={handleDeploy}
                onDelete={(s) => setDeleteTarget({ id: s.id, name: s.name, strategyMode: s.strategy_mode })}
              />
            );
          })}
        </div>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title={['MASTER_EXTERNAL', 'MASTER_MANUAL'].includes(String(deleteTarget?.strategyMode || '').toUpperCase())
          ? 'Delete Master Strategy'
          : 'Delete Strategy'}
        message={['MASTER_EXTERNAL', 'MASTER_MANUAL'].includes(String(deleteTarget?.strategyMode || '').toUpperCase())
          ? <>Delete this Master strategy?<br /><br />This will permanently delete the strategy and its saved setup.</>
          : `Permanently delete "${deleteTarget?.name}"? All deployments, signals and logs will be removed. This cannot be undone.`}
        confirmLabel="Delete"
        submitting={deleteMut.isPending}
        submittingLabel="Deleting…"
        danger
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          if (deleteMut.isPending) return;
          setDeleteTarget(null);
        }}
      />

      {modeSwitchBlock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-bg-card border border-warn/40 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-bg-border bg-warn/5">
              <div className="p-2 rounded-lg bg-warn/15 border border-warn/30">
                <AlertTriangle size={15} className="text-warn" />
              </div>
              <div>
                <h3 className="font-semibold text-txt-primary text-sm">
                  Open position in different mode
                </h3>
                <p className="text-2xs font-mono text-warn mt-0.5">
                  {modeSwitchBlock.strategy_name || 'Strategy'} · DEP:{modeSwitchBlock.deployment_id}
                </p>
              </div>
            </div>

            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-txt-secondary leading-relaxed">
                {modeSwitchBlock.message}
              </p>

              {(modeSwitchBlock.blocking_positions || []).length > 0 && (
                <div className="bg-bg-hover border border-bg-border rounded-lg p-3 space-y-1.5">
                  {modeSwitchBlock.blocking_positions.map((p) => (
                    <div key={p.symbol} className="text-2xs font-mono text-txt-secondary flex justify-between gap-3">
                      <span className="truncate">{p.symbol}</span>
                      <span className="text-txt-muted shrink-0">
                        {p.opened_in_mode} · {p.position_status}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-2xs text-txt-muted font-mono leading-relaxed">
                We can send the exit through the original mode first, then start in{' '}
                <span className="text-txt-primary">{modeSwitchBlock.requested_mode}</span>.
              </p>
            </div>

            <div className="px-5 py-3 border-t border-bg-border flex justify-end gap-2">
              <button
                onClick={() => setModeSwitchBlock(null)}
                disabled={exitAndStartMut.isPending}
                className="btn-ghost text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleExitAndStart}
                disabled={exitAndStartMut.isPending}
                className="btn-primary text-xs flex items-center gap-1.5"
              >
                {exitAndStartMut.isPending ? <Spinner size={12} /> : <Send size={12} />}
                Send Exit Alert + Start {modeSwitchBlock.requested_mode}
              </button>
            </div>
          </div>
        </div>
      )}

      <BrokerTokenGateModal
        open={!!tokenGatePending}
        reason={brokerStatus?.token_status === 'NOT_SET' ? 'not_set' : 'expired'}
        onSuccess={() => {
          const pending = tokenGatePending;
          setTokenGatePending(null);
          if (!pending) return;
          if (typeof pending.resume === 'function') pending.resume();
          else handleDeploy(pending.strategy, pending.executionMode, pending.schedulerState);
        }}
        onDismiss={() => setTokenGatePending(null)}
      />
    </div>
  );
}
